import { Controller } from "@hotwired/stimulus"
// html2canvas-pro, not the original html2canvas: the original can't read the
// oklch()/oklab() colours Tailwind v4 produces (every `/10`-style opacity class,
// the default palette), so it threw on nearly every page, light or dark, and
// the screenshot came out as a blank grey card.
import html2canvas from "html2canvas-pro"
import { track } from "../lib/mixpanel"
import { hideIntercom, showIntercom } from "../helpers/intercom"

// Instant feedback (Phelipe 2026-09-23). Opened from the avatar menu on any
// page: a right-hand panel that stays open while the page is used. Three
// ways in — draw on the page (the screenshot is taken the moment the mouse
// is released, ink included), record the screen (browser's own recorder +
// microphone; a yellow ring follows the mouse, drawing works while it
// records), or just write. Files can be dropped on the panel. AI drafts the
// title, the type and a priority from the words (voice first for a
// recording). Send → a CustomerRequest on the company's Feedback list.
// The element is turbo-permanent, so a recording keeps going across pages.
const MAX_SECONDS = 180 // 3 minutes (Phelipe 2026-10-06; 2 until then) — about 18–26 MB, well inside the AI's 64 MB
// A Web Audio graph the browser has paused (Chrome says "interrupted" on some systems).
const AUDIO_STUCK = new Set(["suspended", "interrupted"])
// How long one ask for the AI write-up may take before the server's write-up takes over.
const SUMMARIZE_TIMEOUT_MS = 75_000
// "Already on the list?" (Kloepfer K9-5): how long Send waits for the check before it just sends.
const DUP_TIMEOUT_MS = 3000
// A Mac makes the browser ask once (System Settings) before it may record a
// window or the screen, then wants the browser reopened: the panel says so.
const ON_MAC = /mac/i.test(navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || "")
const KINDS = ["feature", "bug", "iteration"]
const PRIOS = ["high", "medium", "low"]
// "Give Feedback" in the Intercom chat (Phelipe 2026-09-24) links to
// /company/feedback?give_feedback=1. Intercom opens a link to its own site in
// the same tab, so that page loads: it goes back to the page the person was
// on (remembered per tab) and opens this panel there, and the chat closes.
const LINK_PARAM = "give_feedback"
const LAST_PAGE = "cl:lastPage"
const OPEN_FROM_LINK = "cl:openFeedback"
const CHANNEL = "conslog-feedback"
// A recording in progress is copied into the browser as it goes (K3-34), so a
// full page load mid-recording keeps what was recorded so far. Per tab.
const REC_SID = "cl:feedbackRec"
const REC_DB = "cl-feedback-recording"
// The Feedback Loop always works (Kloepfer K13-28, Larry's Oct 27 call): the
// last page before the Feedback page (Record from there goes back to it), the
// panel's own log of each step, and how long a step may take before ConsLog
// tells the team itself.
const PREV_PAGE = "cl:feedbackPrevPage"
const STEP_LOG = "cl:feedbackSteps"
const WATCH_MS = { upload: 120_000, ai: 90_000, send: 30_000 }
const SLOW_TOLD = "This is taking longer than it should. We've told the team, and your recording is saved."

export default class extends Controller {
  static targets = ["panel", "body", "back", "layer", "ink", "hint", "halo", "pill", "timer", "drawBtn", "wave", "micBtn", "micMenu"]
  static values = { presignUrl: String, createUrl: String, suggestUrl: String, summarizeUrl: String, similarUrl: String, listUrl: String, recorderUrl: String, i18n: Object }

  connect() {
    if (this.element.dataset.wired) return // turbo-permanent: connect can fire again
    this.element.dataset.wired = "1"
    this.state = "closed"
    this.strokes = []
    this.form = this._blankForm()
    this._steps = this._loadSteps()
    this._unsent = []
    this._reported = new Set()
    this._onOpen = (e) => this.open(e.detail?.mode, e.detail)
    window.addEventListener("instant-feedback:open", this._onOpen)
    // A draft row on the Feedback Loop list reopens here (Phelipe 2026-09-23).
    this._onResume = (e) => {
      const id = +e.detail?.id
      if (!id) return
      if (e.detail.host && !this.recording) this._enterHost(e.detail.host)
      this.resumeFromList(id)
    }
    window.addEventListener("instant-feedback:resume", this._onResume)
    // Inside the Ask AI window (Phelipe 2026-10-17): its × closes the form too,
    // and the form follows the window when the browser is resized.
    this._onLeave = () => { if (this._host && this.panelOpen && !this.recording && this.state !== "draw") this.close() }
    window.addEventListener("instant-feedback:leave", this._onLeave)
    // The window's ‹ while hosted (Phelipe 2026-10-17, "no need for two titles"): the
    // form's own title row is hidden, so ‹ is the form's back on an inner step and
    // leaves the form from its first screen.
    this._onBack = () => {
      if (!this._host || !this.panelOpen || this.recording || this.state === "draw") return
      this.state === "choose" || this.state === "sent" ? this.close() : this.back()
    }
    window.addEventListener("instant-feedback:back", this._onBack)
    this._onResize = () => { if (this._host && this.panelOpen) this._placeHosted() }
    window.addEventListener("resize", this._onResize)
    // Studio's "Draw where" (2026-10-17): the same draw-on-the-page flow, but the
    // picture goes back to whoever asked — no form, no request. Listen for
    // "instant-feedback:drawing" (layer is up, step aside), then
    // "instant-feedback:drawn" {dataUrl: JPEG} or "instant-feedback:draw-cancelled".
    this._onDrawFor = (e) => {
      if (this.recording || this.state === "draw") return
      if (this.panelOpen) this.close()
      this._drawFor = e.detail?.host || "studio"
      window.dispatchEvent(new CustomEvent("instant-feedback:drawing", { detail: { host: this._drawFor } }))
      this.startDraw()
    }
    window.addEventListener("instant-feedback:draw", this._onDrawFor)
    this._onKey = (e) => {
      if (e.key !== "Escape") return
      if (this.hasMicMenuTarget && !this.micMenuTarget.classList.contains("hidden")) { this._closeMicMenu(); return }
      if (this.state === "draw") this.cancelDraw()
      else if (this.panelOpen && !this.recording) this.close()
    }
    document.addEventListener("keydown", this._onKey)
    this._onMove = (e) => { if (this.recording) { this.haloTarget.style.left = `${e.clientX}px`; this.haloTarget.style.top = `${e.clientY}px` } }
    document.addEventListener("pointermove", this._onMove)
    // Closing the tab mid-upload loses the file — until it's up, a recording
    // lives only in this browser — so the browser asks first (2026-09-24).
    // Moving to another ConsLog page is fine: this element outlives it.
    // The same while recording (K3-34): a full page load ends the screen share.
    this._uploadsInFlight = 0
    this._onUnload = (e) => { if (this._uploadsInFlight > 0 || this.recording) { e.preventDefault(); e.returnValue = "" } }
    window.addEventListener("beforeunload", this._onUnload)
    this._wireInk()
    this._wireDrop()
    this._fromLink()
    this._rememberPage()
    this._onVisit = () => { this._passOn = null; this._rememberPage(); this._watchChat?.() }
    document.addEventListener("turbo:load", this._onVisit)
    this._watchIntercom()
    this._keepRecordingOnLinks()
    this._recoverRecording()
  }

  disconnect() {
    window.removeEventListener("instant-feedback:open", this._onOpen)
    window.removeEventListener("instant-feedback:resume", this._onResume)
    window.removeEventListener("instant-feedback:leave", this._onLeave)
    window.removeEventListener("instant-feedback:back", this._onBack)
    window.removeEventListener("resize", this._onResize)
    window.removeEventListener("instant-feedback:draw", this._onDrawFor)
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("pointermove", this._onMove)
    window.removeEventListener("beforeunload", this._onUnload)
    document.removeEventListener("turbo:load", this._onVisit)
    if (this._onNavigate) window.navigation?.removeEventListener("navigate", this._onNavigate)
    if (this._onLeavePage) window.navigation?.removeEventListener("navigate", this._onLeavePage)
    this._bodyObserver?.disconnect()
    this._frameObserver?.disconnect()
    this._channel?.close()
    delete this.element.dataset.wired
  }

  // ── the Give Feedback link ──────────────────────────────────────────
  // Intercom opens the link in a NEW tab, which remembers nothing: it hands
  // the panel to the tab whose chat is open (that tab opens it where the
  // person is and closes the chat) and closes itself. A tab that remembers a
  // page got the link in the same tab: it goes back there and opens it.
  async _fromLink() {
    const url = new URL(location.href)
    if (url.searchParams.has(LINK_PARAM)) {
      url.searchParams.delete(LINK_PARAM)
      const here = url.pathname + url.search + url.hash
      const back = this._session(LAST_PAGE)
      if (back && back !== here && this._session(OPEN_FROM_LINK, "1")) return location.replace(back)
      if (!back && (await this._handOff())) {
        window.close()
        await new Promise((resolve) => setTimeout(resolve, 400)) // still here: the browser kept the tab open
      }
      history.replaceState(history.state, "", here)
      this._rememberPage()
      return this._openFromLink()
    }
    if (this._session(OPEN_FROM_LINK) === "1") {
      this._session(OPEN_FROM_LINK, null)
      this._openFromLink()
    }
  }

  // Asks the other ConsLog tabs; the one whose chat is open answers.
  _handOff() {
    if (typeof BroadcastChannel !== "function") return Promise.resolve(false)
    return new Promise((resolve) => {
      const ch = new BroadcastChannel(CHANNEL), id = `${Date.now()}-${Math.random()}`
      const done = (ok) => { clearTimeout(timer); ch.close(); resolve(ok) }
      const timer = setTimeout(() => done(false), 700)
      ch.onmessage = (e) => { if (e.data?.type === "opened" && e.data.id === id) done(true) }
      ch.postMessage({ type: "open", id })
    })
  }

  // The same link without leaving the page ("the user stays on the page",
  // Phelipe 2026-09-24). Intercom draws its chat in frames it fills from this
  // page, so the Give Feedback click is seen here first, however Intercom
  // follows it: a click on the link inside the chat, a window.open of it, or
  // this tab navigating to it. Each one opens the panel and closes the chat
  // instead. What still gets through lands in a new tab, which hands the
  // panel back to this one (above).
  _watchIntercom() {
    // Intercom may send the click through its own tracking address, so the
    // link is known by its address, its data-via, or its words.
    const isLink = (url) => String(url || "").includes(LINK_PARAM)
    const take = (e) => { e?.preventDefault(); this._openFromLink(); return null }
    const wrapOpen = (w) => {
      if (!w || w.__clFeedbackOpen) return
      const open = w.open
      w.open = function (url, ...rest) { return isLink(url) ? take() : open.call(this, url, ...rest) }
      w.__clFeedbackOpen = true
    }
    // Capture, so it runs before Intercom's own click handler.
    // Intercom draws its home buttons as links or as clickable boxes.
    const onClick = (e) => {
      const a = e.target.closest?.('a, button, [role="button"]')
      if (!a) return
      const words = (a.textContent || "").trim().toLowerCase()
      if (isLink(a.getAttribute("href")) || isLink(a.getAttribute("data-via")) || words.startsWith("give feedback")) {
        e.stopImmediatePropagation()
        take(e)
      }
    }
    this._hookFrames = () => document.querySelectorAll('iframe[name^="intercom"], iframe[id^="intercom"]').forEach((f) => {
      try {
        f.contentDocument?.addEventListener("click", onClick, true) // the same listener twice is a no-op
        wrapOpen(f.contentWindow)
      } catch (_) { /* a frame from another site: nothing to catch there */ }
    })
    // A frame's page can be rewritten right after it appears (which drops
    // listeners), so hook again a few times.
    this._hookSoon = () => [0, 300, 1000, 2500].forEach((ms) => setTimeout(this._hookFrames, ms))
    wrapOpen(window)
    if (window.navigation?.addEventListener) {
      this._onNavigate = (e) => { if (e.cancelable && isLink(e.destination?.url)) take(e) }
      window.navigation.addEventListener("navigate", this._onNavigate)
    }
    // Whether this tab's chat is open: the hand-off goes to that tab.
    const listen = () => {
      if (typeof window.Intercom !== "function") return false
      window.Intercom("onShow", () => { this._chatOpen = true; this._hookSoon() })
      window.Intercom("onHide", () => { this._chatOpen = false })
      return true
    }
    if (!listen()) window.addEventListener("load", listen, { once: true })
    if (typeof BroadcastChannel === "function") {
      this._channel = new BroadcastChannel(CHANNEL)
      this._channel.onmessage = (e) => {
        const m = e.data || {}
        if (m.type !== "open" || !this._chatOpen) return
        this._openFromLink()
        this._channel.postMessage({ type: "opened", id: m.id })
      }
    }
    this._watchChat()
  }

  // Intercom builds the chat inside #intercom-container, and Turbo swaps the
  // <body> on every visit (Intercom then rebuilds the container), so this
  // watches the body for a new container and the container for new frames.
  _watchChat() {
    if (!this._hookFrames) return
    this._bodyObserver?.disconnect()
    this._bodyObserver = new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes].some((n) => n.id === "intercom-container"))) this._watchBox()
    })
    this._bodyObserver.observe(document.body, { childList: true })
    this._watchBox()
    this._hookFrames()
    this._hookSoon()
  }

  _watchBox() {
    this._frameObserver?.disconnect()
    this._frameObserver = null
    const box = document.getElementById("intercom-container")
    if (!box) return
    this._frameObserver = new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes].some((n) => n.nodeName === "IFRAME" || n.querySelector?.("iframe")))) this._hookSoon()
    })
    this._frameObserver.observe(box, { childList: true, subtree: true })
    this._hookSoon()
  }

  _openFromLink() {
    this.open()
    // Close the chat. Before Intercom has loaded, the call waits in its queue.
    const hide = () => { try { window.Intercom?.("hide") } catch (_) {} }
    hide()
    setTimeout(hide, 1500)
  }

  _rememberPage() {
    if (new URLSearchParams(location.search).has(LINK_PARAM)) return
    const here = location.pathname + location.search + location.hash
    this._session(LAST_PAGE, here)
    if (!this._onFeedbackPage()) this._session(PREV_PAGE, here)
  }

  // The company's Feedback page itself (the list, a request on it).
  _onFeedbackPage() {
    const list = (this.listUrlValue || "").replace(/\/$/, "")
    return !!list && (location.pathname === list || location.pathname.startsWith(`${list}/`))
  }

  // sessionStorage can refuse (a private window, blocked storage). One
  // argument reads; two write (null removes) and say whether it stuck.
  _session(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(key)
      if (value === null) sessionStorage.removeItem(key)
      else sessionStorage.setItem(key, value)
      return true
    } catch (_) {
      return value === undefined ? null : false
    }
  }

  // ── a recording across pages (K3-34) ─────────────────────────────────
  // This element is turbo-permanent, so a recording keeps going on Turbo
  // visits. Some ConsLog links load the whole page instead (a click on a
  // meeting row did), which ends the browser's screen share. While recording,
  // those go through Turbo too. One that truly needs a full load (Turbo hands
  // it back) still goes — the browser asks first — and what was recorded so
  // far comes back in the panel on the new page (below).
  // A page that reloads itself (K13-28: about 155 actions in ConsLog do,
  // Timesheets among them) is redrawn through Turbo the same way, so the
  // recording keeps going and the browser never asks "Leave site?".
  _keepRecordingOnLinks() {
    if (!window.navigation?.addEventListener) return
    this._onLeavePage = (e) => {
      if (!this.recording || !window.Turbo?.visit || e.defaultPrevented || !e.cancelable) return
      if (e.destination?.sameDocument || e.hashChange || e.downloadRequest != null || e.formData) return
      const reload = e.navigationType === "reload"
      if (!reload && e.navigationType !== "push" && e.navigationType !== "replace") return
      let url
      try { url = new URL(reload ? location.href : e.destination.url) } catch (_) { return }
      if (url.origin !== location.origin) return
      if (/\.(?!html?$)[a-z0-9]{2,5}$/i.test(url.pathname)) return // a file (PDF, spreadsheet…)
      const now = Date.now()
      if (this._passOn && this._passOn.url === url.href && now - this._passOn.at < 10000) return // Turbo gave it back: it needs a full load
      this._passOn = { url: url.href, at: now }
      e.preventDefault()
      if (reload) this._step("reload_kept", url.pathname)
      window.Turbo.visit(url.href, { action: reload || e.navigationType === "replace" ? "replace" : "advance" })
    }
    window.navigation.addEventListener("navigate", this._onLeavePage)
  }

  // The recording's pieces go into the browser's own storage as they come.
  // The tab holds a lock while it records, so another open tab never takes
  // over a recording that is still going.
  _backupStart(session, mime) {
    this._session(REC_SID, session.sid)
    try { navigator.locks?.request?.(`${REC_DB}-${session.sid}`, () => new Promise((resolve) => { session.releaseLock = resolve }))?.catch(() => {}) } catch (_) { /* no locks: the tab's own storage still keeps it apart */ }
    const c = this.commentFor
    session.meta = { sid: session.sid, mime, mic: !!this.form.recMic, at: Date.now(), seconds: 0, commentFor: c ? { id: c.id, title: c.title } : null }
    this._idbRun("readwrite", (tx) => { tx.objectStore("meta").put(session.meta) })
  }

  _backupChunk(session, blob) {
    if (session.done || !session.meta) return
    const meta = { ...session.meta, seconds: session.seconds, mic: !!this.form.recMic }
    this._idbRun("readwrite", (tx) => { tx.objectStore("parts").add({ sid: session.sid, blob }); tx.objectStore("meta").put(meta) })
  }

  _dropBackup(sid) {
    if (!sid) return
    if (this._session(REC_SID) === sid) this._session(REC_SID, null)
    this._idbRun("readwrite", (tx) => {
      tx.objectStore("meta").delete(sid)
      const cur = tx.objectStore("parts").openCursor()
      cur.onsuccess = () => { const c = cur.result; if (!c) return; if (c.value?.sid === sid) c.delete(); c.continue() }
    })
  }

  // The page loaded while this tab was recording: what was recorded so far
  // opens in the panel, uploads and is written up like any recording.
  async _recoverRecording() {
    const sid = this._session(REC_SID)
    if (!sid) return
    let free = true
    try { if (navigator.locks?.request) free = await navigator.locks.request(`${REC_DB}-${sid}`, { ifAvailable: true }, (lock) => !!lock) } catch (_) { free = true }
    if (!free) return // still recording somewhere
    let meta = null
    const blobs = []
    await this._idbRun("readonly", (tx) => {
      const m = tx.objectStore("meta").get(sid)
      m.onsuccess = () => { meta = m.result || null }
      const cur = tx.objectStore("parts").openCursor()
      cur.onsuccess = () => { const c = cur.result; if (!c) return; if (c.value?.sid === sid && c.value.blob) blobs.push(c.value.blob); c.continue() }
    })
    this._dropBackup(sid)
    if (!meta || !blobs.length || Date.now() - (meta.at || 0) > 86_400_000 || this.recording) return
    const blob = new Blob(blobs, { type: meta.mime || blobs[0].type || "video/webm" })
    if (!blob.size) return
    this.form = this._blankForm()
    this.form.recMic = !!meta.mic
    this.commentFor = meta.commentFor?.id ? { id: +meta.commentFor.id, title: meta.commentFor.title || "" } : null
    this.lastComment = false
    this.state = "recovered" // open() keeps this form
    this._toast(this.i18nValue.recording_recovered || "The page reloaded and the recording stopped. Here's what was recorded — you can send it.")
    this._step("recording_cut", `full page load after ${meta.seconds || 0}s`)
    this._useRecording(blob, meta.seconds || 0, { cut: true })
  }

  _idb() {
    if (!this._idbP) {
      this._idbP = new Promise((resolve) => {
        try {
          const req = indexedDB.open(REC_DB, 1)
          req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains("parts")) db.createObjectStore("parts", { autoIncrement: true })
            if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "sid" })
          }
          req.onsuccess = () => resolve(req.result)
          req.onerror = () => resolve(null)
          req.onblocked = () => resolve(null)
        } catch (_) { resolve(null) }
      })
    }
    return this._idbP
  }

  // One transaction over both stores; resolves when it's done (or failed — storage is a nice-to-have here).
  async _idbRun(mode, fn) {
    const db = await this._idb()
    if (!db) return false
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(["parts", "meta"], mode)
        tx.oncomplete = () => resolve(true)
        tx.onerror = tx.onabort = () => resolve(false)
        fn(tx)
      } catch (_) { resolve(false) }
    })
  }

  // ── panel ───────────────────────────────────────────────────────────
  // opts.comment_for (Phelipe 2026-10-09): record (or write) an answer that lands as a
  // comment on that request instead of a new one — the recorder opens first.
  // opts.host = "studio" (Phelipe 2026-10-17): opened from the Ask AI window, the
  // form shows inside that window instead of sliding in from the side — see
  // _enterHost. Opened from anywhere else it is the side panel as before.
  open(mode, opts = null) {
    if (opts?.host) this._enterHost(opts.host)
    const wasOpen = this.panelOpen
    this.panelOpen = true
    if (this._host) this._showHosted()
    else this.panelTarget.classList.remove("translate-x-full")
    this.panelTarget.setAttribute("aria-hidden", "false")
    // The support chat bubble covered Save draft and the bottom of the form
    // (Larry K10-1, 2026-10-20): it steps aside while the panel is open, and
    // stays aside while they record or draw ("aside" below).
    if (!wasOpen) hideIntercom()
    if (this.state === "closed" || this.state === "sent" || this.state === "choose") {
      this.form = this._blankForm()
      this.lastComment = false
      this.commentFor = opts?.comment_for ? { id: +opts.comment_for, title: opts.title || "" } : null
      // Ask AI's "Report it as an issue" (Studio, 2026-10-16): the words they
      // typed wait in the box whichever way they pick; the request is marked
      // as coming from Studio (source → origin "studio" on the server).
      if (opts?.prefill) this.form.desc = String(opts.prefill).slice(0, 4000)
      // The chat's Feedback Loop card (Phelipe 2026-10-17) hands over its title
      // and kind too; they count as the person's own, so the AI leaves them.
      if (opts?.prefill_title) { this.form.title = String(opts.prefill_title).slice(0, 200); this.form.titleTouched = true }
      if (opts?.prefill_kind && KINDS.includes(opts.prefill_kind)) { this.form.kind = opts.prefill_kind; this.form.kindTouched = true }
      // A failed drone flight's "Tell us more" (2026-10-28) says what the note is about; it rides along in the context.
      this.form.extra = opts?.context && typeof opts.context === "object" ? opts.context : null
      this.source = opts?.source || null
      // mode "record": straight into the screen recording, as if they had
      // pressed Record on the form; when it stops, the form opens with the video.
      this._setState(this.commentFor || mode === "record" ? "recpre" : mode === "text" ? "text" : "choose")
      if (mode === "record" && !this.commentFor) this.startRec()
    }
  }

  // `reason` "aside": stepping out of the way to draw or record — the form (and
  // the Ask AI window hosting it) comes back when that is done. Anything else
  // is a real close.
  close(reason = null) {
    if (reason !== "aside") this._hideDup()
    const wasOpen = this.panelOpen
    this.panelOpen = false
    this.panelTarget.setAttribute("aria-hidden", "true")
    if (this._host) {
      this.panelTarget.style.display = "none"
      if (reason === "aside") this._hostEvent("hidden")
      else this._leaveHost()
    } else {
      this.panelTarget.classList.add("translate-x-full")
    }
    if (this.state !== "draw" && this.state !== "recording") this.state = "closed"
    if (wasOpen && reason !== "aside") showIntercom()
  }

  // ── inside the Ask AI window ───────────────────────────────────────
  // The form is still this controller's own panel (its targets must stay in
  // here), drawn over the window's body: same left, width and bottom, from
  // under the window's header down. The window marks those two elements with
  // data-studio-chat-host / data-studio-chat-host-header and listens to
  // instant-feedback:host {phase: shown | hidden | left} to show, step aside
  // with the form, and get its own screens back.
  _enterHost(name) {
    if (this._host) return
    const el = this.panelTarget
    this._host = { name, className: el.className, style: el.getAttribute("style") || "" }
    el.dataset.hosted = name
    el.className = "fixed bg-white z-[80] flex flex-col"
    // One title only: the window's bar already says Feedback Loop with ‹ and ×.
    if (el.firstElementChild) el.firstElementChild.hidden = true
  }

  _showHosted() {
    this._hostEvent("shown")
    if (!this._placeHosted()) { this._leaveHost(); this.panelTarget.classList.remove("translate-x-full") }
  }

  _placeHosted() {
    const host = document.querySelector("[data-studio-chat-host]")
    if (!host || !host.offsetWidth) return false
    const r = host.getBoundingClientRect()
    const head = host.querySelector("[data-studio-chat-host-header]")
    const top = head ? head.getBoundingClientRect().bottom : r.top
    const el = this.panelTarget
    el.style.display = ""
    el.style.top = `${Math.round(top)}px`
    el.style.left = `${Math.round(r.left)}px`
    el.style.width = `${Math.round(r.width)}px`
    el.style.height = `${Math.round(r.bottom - top)}px`
    el.style.borderRadius = "0 0 16px 16px"
    el.style.transform = "none"
    el.style.transition = "none"
    return true
  }

  _leaveHost() {
    const h = this._host
    if (!h) return
    const el = this.panelTarget
    el.className = h.className
    if (h.style) el.setAttribute("style", h.style); else el.removeAttribute("style")
    el.classList.add("translate-x-full")
    if (el.firstElementChild) el.firstElementChild.hidden = false
    delete el.dataset.hosted
    this._host = null
    window.dispatchEvent(new CustomEvent("instant-feedback:host", { detail: { phase: "left", host: h.name } }))
  }

  _hostEvent(phase) {
    window.dispatchEvent(new CustomEvent("instant-feedback:host", { detail: { phase, host: this._host?.name } }))
  }

  back() {
    if (this.form.rec?.url) URL.revokeObjectURL(this.form.rec.url)
    this.form = this._blankForm()
    this._setState("choose")
  }

  _blankForm() {
    return { kind: "feature", prio: "medium", title: "", desc: "", files: [], shot: null, rec: null,
      titleTouched: false, kindTouched: false, prioTouched: false, aiFilled: false, why: "",
      draftId: null, removeIds: [], extra: null,
      // K9-5: what the typing step said about look-alikes ({ key, match }), and the words
      // they already chose "Send as new" for (the check doesn't ask twice).
      similar: null, dupSkip: null }
  }

  _setState(s) {
    this.state = s
    this.backTarget.classList.toggle("hidden", s === "choose" || s === "sent")
    this._render()
  }

  // What the request will carry about where the person is — read at open
  // time, since this element outlives page changes.
  _context() {
    let meta = {}
    try { meta = JSON.parse(document.querySelector('meta[name="feedback-context"]')?.content || "{}") } catch (e) { meta = {} }
    const ua = navigator.userAgent
    const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "Browser"
    const os = /Mac/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : ""
    return { page: meta.page || document.title.split(" | ")[0], path: location.pathname, project_id: meta.project_id || "",
      project: meta.project || "", browser: `${browser}${os ? " on " + os : ""}`, viewport: `${innerWidth}×${innerHeight}`, locale: document.documentElement.lang || "",
      ...(this.form?.extra || {}) }
  }

  _contextLine() {
    const c = this._context()
    return [c.page, c.project, c.browser].filter(Boolean).join(" · ")
  }

  // ── rendering ───────────────────────────────────────────────────────
  _render() {
    const t = this.i18nValue, esc = this._esc, s = this.state
    let h = ""
    // Answering a request (2026-10-09): say which one, above whatever step this is.
    const answering = this.commentFor && s !== "sent" ? `<p class="m-0 rounded-lg bg-amber-50 text-amber-900 px-2.5 py-1.5 text-[13px]" data-answering>${esc((t.comment_banner || "Answering: %{title}").replace("%{title}", this.commentFor.title))}</p>` : ""
    if (s === "choose") {
      // Record my screen first, then Screenshot / Draw (Phelipe 2026-10-06).
      h = `<p class="m-0 text-text-muted">${esc(t.subtitle)}</p>
        ${this._tile("recpre", "video", t.record, t.record_hint)}
        ${this._tile("draw", "pencil", t.draw, t.draw_hint)}
        ${this._tile("text", "text", t.write, t.write_hint)}
        <div data-recent></div>`
    } else if (s === "shot") {
      h = `<div class="rounded-lg border border-gray-300 overflow-hidden bg-gray-100">${this.form.shot ? `<img src="${this.form.shot.dataUrl}" alt="" class="block w-full h-auto">` : ""}</div>
        <div class="flex flex-wrap items-center gap-2 text-[12.5px] text-text-muted"><button type="button" class="rounded-lg border border-gray-300 bg-white px-2.5 py-1 font-semibold text-gray-600 cursor-pointer" data-act="draw">${esc(t.draw_again)}</button><span>${esc(t.on_screenshot)}</span></div>
        ${this._formHtml()}`
    } else if (s === "recpre") {
      const ok = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia && window.MediaRecorder)
      h = `<p class="m-0 text-text-muted">${esc(t.record_intro)}</p>
        ${ok && ON_MAC && t.mac_tip ? `<p class="m-0 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 text-[13px] px-3 py-2" data-mac-tip>${esc(t.mac_tip)}</p>` : ""}
        ${ok ? `<label class="flex items-center gap-2 text-[13px] cursor-pointer"><input type="checkbox" data-mic checked class="w-4 h-4 accent-brand"> <span>${esc(t.mic)}</span></label>
        <button type="button" class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand hover:bg-brand-dark text-white font-semibold py-2 cursor-pointer" data-act="startRec">${this._icon("video")} ${esc(t.start_recording)}</button>`
          : `<p class="m-0 rounded-lg bg-red-50 text-red-800 px-3 py-2">${esc(t.not_supported)}</p>`}`
    } else if (s === "rec") {
      const r = this.form.rec, busy = this.form.recBusy !== false
      // The recording shows at once with playback controls (review it), and a
      // strip on the video says what's happening: uploading → listening → written.
      // Inside the chat window the strip is left out (Phelipe 2026-10-17): the
      // pill next to Record again already says it, and the thumbnail stays clean.
      // shrink-0 (Phelipe 2026-10-17): the body is a flex column, and with
      // overflow-hidden this box could be squeezed to a sliver once the form
      // grew taller than the panel — the video "disappeared" in the chat window.
      h = `<div class="relative rounded-lg overflow-hidden bg-gray-900 shrink-0" data-controller="video-speed">${r?.url ? `<video src="${r.url}" controls playsinline preload="metadata" class="block w-full max-h-[240px]"></video>` : `<div class="h-[140px]"></div>`}
          ${this._host ? "" : `<div class="${busy ? "" : "hidden "}absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gray-900/85 text-gray-50 text-[13px] px-3 py-1.5" data-recstatus><span class="w-3 h-3 rounded-full border-2 border-gray-400 border-t-gray-50 animate-spin shrink-0" data-recspin></span><span data-recstatus-text>${esc(this.form.recStatus || t.uploading_recording || t.uploading)}</span></div>`}</div>
        <div class="flex items-center gap-2 text-[12.5px] text-text-muted"><button type="button" class="shrink-0 rounded-lg border border-gray-300 bg-white px-2 py-1 font-semibold text-gray-600 cursor-pointer whitespace-nowrap" data-act="recpre">${esc(t.record_again)}</button><span class="inline-flex items-center gap-1 min-w-0 text-purple-800 bg-purple-50 rounded-full px-2 py-px text-[11px] font-medium" data-listening><span class="shrink-0 inline-flex">${this._icon("spark")}</span><span class="truncate">${esc(this.form.recStatus || (this.form.recMic ? t.listening : t.watching))}</span></span></div>
        ${this._formHtml()}`
    } else if (s === "text") {
      h = this._formHtml()
    } else if (s === "sent") {
      h = `<div class="text-center pt-6 pb-2">
          <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-800 mb-3">${this._icon("check")}</div>
          <h3 class="m-0 mb-1 text-[16px] font-bold text-text-heading">${esc(this.lastComment ? (t.sent_comment_title || "Added — thank you") : this.lastReview && t.sent_review_title ? t.sent_review_title : t.sent_title)}</h3>
          <p class="m-0 mb-4 text-text-muted">${esc(this.lastComment ? (t.sent_comment_body || "Your answer is on the request as a comment. The team sees it right away.") : this.lastReview && t.sent_review_body ? t.sent_review_body : t.sent_body)}</p>
          ${this.aiLater && t.sent_ai_later ? `<p class="m-0 mb-4 rounded-lg bg-purple-50 text-purple-900 px-3 py-2 text-[13px] text-left">${this._icon("spark")} ${esc(t.sent_ai_later)}</p>` : ""}
          ${this._lateHtml()}
          <div class="flex flex-col gap-2">
            <button type="button" class="w-full rounded-lg bg-brand hover:bg-brand-dark text-white font-semibold py-2 cursor-pointer" data-act="seeIt">${esc(t.see_it)}</button>
            ${this.lastPublicUrl ? `<button type="button" class="w-full rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold py-2 cursor-pointer" data-act="copyLink" data-copy-link>${esc(t.copy_link)}</button>` : ""}
            <button type="button" class="w-full rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold py-2 cursor-pointer" data-act="again">${esc(t.send_another)}</button>
          </div></div>
        <div data-recent></div>`
    }
    this.bodyTarget.innerHTML = answering + h
    this._wire()
    if (s === "choose" || s === "sent") this._loadRecent()
  }

  _tile(act, icon, title, hint) {
    // Feedback Loop green, like its card in the chat window (Phelipe 2026-10-17).
    return `<button type="button" class="flex items-center gap-3 w-full text-left rounded-xl border border-gray-300 bg-white hover:border-green-500 hover:bg-green-50 px-3.5 py-3 cursor-pointer text-text-heading" data-act="${act}">
      <span class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-50 text-green-600 shrink-0">${this._icon(icon)}</span>
      <span><b class="block text-[14px] font-semibold">${this._esc(title)}</b><small class="block text-[12.5px] text-text-muted mt-px">${this._esc(hint)}</small></span></button>`
  }

  _formHtml() {
    const t = this.i18nValue, esc = this._esc, f = this.form
    // An answer on a request (2026-10-09): just the words and the files — no title, type or priority.
    if (this.commentFor) return `
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.comment_words || "A few words")} <span class="font-normal text-text-muted">— ${esc(t.optional)}</span></label>
        <textarea class="w-full min-h-[72px] rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-[13.5px] resize-y" data-field="desc" placeholder="${esc(t.comment_placeholder_panel || "Anything the recording doesn't show…")}">${esc(f.desc)}</textarea></div>
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.attach)} <span class="font-normal text-text-muted">— ${esc(t.optional)}</span></label>
        <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-[12.5px] text-text-muted flex flex-wrap items-center gap-1" data-drop>
          <input type="file" multiple hidden data-fileinput><button type="button" class="text-brand font-semibold underline cursor-pointer" data-act="pick">${esc(t.choose_files)}</button><span>${esc(t.drop_hint)}</span></div>
        <div class="flex flex-wrap gap-1.5 mt-2 empty:hidden" data-chips></div></div>
      <p class="hidden m-0 text-[12.5px] text-red-700" data-err></p>
      <div class="sticky bottom-0 -mx-4 px-4 pt-2.5 pb-1 bg-white border-t border-gray-100 flex flex-col gap-1.5" data-send-row><button type="button" class="w-full rounded-lg bg-brand hover:bg-brand-dark text-white font-semibold py-2 cursor-pointer disabled:opacity-60" data-act="send">${esc(t.comment_send || "Send answer")}</button></div>`
    const opts = (name, keys, labels, cur) => keys.map(k => `<button type="button" class="rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[12.5px] font-medium text-gray-600 cursor-pointer data-[on=true]:bg-gray-800 data-[on=true]:text-white data-[on=true]:border-gray-800" data-opt="${name}" data-v="${k}" data-on="${cur === k}">${esc(labels[k])}</button>`).join("")
    // hidden ATTRIBUTE: the .hidden class loses to .inline-flex in Tailwind v4's compiled order.
    const ai = (id, label) => `<span hidden class="ml-1 align-middle inline-flex items-center gap-0.5 text-[10px] leading-[15px] font-medium text-purple-700 bg-purple-50 rounded-full px-1" data-ai="${id}"><span class="text-[9px]">✨</span>${esc(label)}</span>`
    // Drafts (Phelipe 2026-09-23): nothing leaves until Send; a saved
    // draft is picked up again from "Your recent feedback".
    const draftNote = f.draftId ? `<p class="m-0 rounded-lg bg-amber-50 text-amber-900 px-2.5 py-1.5 text-[13px]">${esc(t.draft_editing || "You're editing a draft. Nothing is sent until you press Send.")}</p>` : ""
    return `${draftNote}
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.describe)} ${ai("desc", t.from_recording)}</label>
        <textarea class="w-full min-h-[88px] rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-[13.5px] resize-y" data-field="desc" placeholder="${esc(t.describe_placeholder)}">${esc(f.desc)}</textarea></div>
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.attach)} <span class="font-normal text-text-muted">— ${esc(t.optional)}</span></label>
        <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-[12.5px] text-text-muted flex flex-wrap items-center gap-1" data-drop>
          <input type="file" multiple hidden data-fileinput><button type="button" class="text-brand font-semibold underline cursor-pointer" data-act="pick">${esc(t.choose_files)}</button><span>${esc(t.drop_hint)}</span></div>
        <div class="flex flex-wrap gap-1.5 mt-2 empty:hidden" data-chips></div></div>
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.title_label)} ${ai("title", t.ai_suggested)}</label>
        <input type="text" class="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-[13.5px]" data-field="title" value="${esc(f.title)}" placeholder="${esc(t.title_placeholder)}"></div>
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.what_is_it)} ${ai("kind", t.ai_suggested)}</label><div class="flex flex-wrap gap-1.5" data-opts="kind">${opts("kind", KINDS, t.kinds, f.kind)}</div></div>
      <div><label class="block text-[12px] font-semibold text-gray-600 mb-1">${esc(t.your_priority)} ${ai("prio", t.ai_suggested)}</label><div class="flex flex-wrap gap-1.5" data-opts="prio">${opts("prio", PRIOS, t.priorities, f.prio)}</div>
        <p class="hidden m-0 mt-1.5 text-[12.5px] text-text-muted" data-why></p></div>
      ${this._host ? "" : `<div class="flex items-start gap-2 rounded-lg bg-gray-50 px-2.5 py-2 text-[12.5px] text-text-muted">${this._icon("pin")}<span>${esc(t.context.replace("%{where}", this._contextLine()))}</span></div>`}
      <p class="hidden m-0 text-[12.5px] text-red-700" data-err></p>
      <div class="sticky bottom-0 -mx-4 px-4 pt-2.5 pb-1 bg-white border-t border-gray-100 flex flex-col gap-1.5" data-send-row>
      <div class="flex items-center gap-2">
        <button type="button" class="flex-1 rounded-lg bg-brand hover:bg-brand-dark text-white font-semibold py-2 cursor-pointer disabled:opacity-60" data-act="send">${esc(this._sendLabel())}</button>
        <button type="button" class="shrink-0 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-3 cursor-pointer disabled:opacity-60" data-act="draft">${esc(t.save_draft || "Save draft")}</button>
      </div>
      ${f.draftId ? `<div class="text-center"><button type="button" class="text-[13px] text-gray-500 hover:text-red-600 underline cursor-pointer" data-act="discard">${esc(t.discard_draft || "Discard draft")}</button></div>` : ""}
      </div>`
  }

  _wire() {
    const b = this.bodyTarget
    b.querySelectorAll("[data-act]").forEach(el => el.onclick = () => this._act(el.dataset.act))
    const desc = b.querySelector("[data-field=desc]"), title = b.querySelector("[data-field=title]")
    if (desc) {
      desc.oninput = () => { this.form.desc = desc.value; this._hideAi("desc"); this._hideDup(); this._queueSuggest() }
      if (!this.form.desc) setTimeout(() => desc.focus(), 40)
    }
    if (title) title.oninput = () => { this.form.title = title.value; this.form.titleTouched = true; this._hideAi("title"); this._hideDup() }
    b.querySelectorAll("[data-opt]").forEach(el => el.onclick = () => {
      const name = el.dataset.opt, v = el.dataset.v
      b.querySelectorAll(`[data-opt=${name}]`).forEach(x => x.dataset.on = String(x === el))
      if (name === "kind") { this.form.kind = v; this.form.kindTouched = true; this._hideAi("kind") }
      else { this.form.prio = v; this.form.prioTouched = true; this._hideAi("prio") }
    })
    const input = b.querySelector("[data-fileinput]")
    if (input) input.onchange = () => { this._addFiles(input.files); input.value = "" }
    const drop = b.querySelector("[data-drop]")
    if (drop) {
      drop.ondragover = e => { e.preventDefault(); e.stopPropagation(); drop.classList.add("border-brand", "bg-brand/10") }
      drop.ondragleave = () => drop.classList.remove("border-brand", "bg-brand/10")
      drop.ondrop = e => { e.preventDefault(); e.stopPropagation(); drop.classList.remove("border-brand", "bg-brand/10"); this._addFiles(e.dataTransfer.files) }
    }
    this._renderChips()
    if (this.form.why) this._showWhy(this.form.why)
    if (this.form.aiFilled) ["title", "kind", "prio", "desc"].forEach(k => this._showAi(k))
  }

  _act(act) {
    switch (act) {
      case "draw": return this.startDraw()
      case "recpre": return this._setState("recpre")
      case "text": return this._setState("text")
      case "startRec": return this.startRec()
      case "pick": return this.bodyTarget.querySelector("[data-fileinput]")?.click()
      case "send": return this.submit()
      case "draft": return this.saveDraft()
      case "discard": return this._askDiscard()
      case "copyLink": return this._copyLink(this.lastPublicUrl, this.bodyTarget.querySelector("[data-copy-link]"))
      case "seeIt": { const id = this.lastId; this.close(); const url = `${this.listUrlValue}${id ? `?highlight=${id}` : ""}`; return window.Turbo ? window.Turbo.visit(url) : location.assign(url) }
      case "again": return this.back()
      case "seeAll": { this.close(); return window.Turbo ? window.Turbo.visit(this.listUrlValue) : location.assign(this.listUrlValue) }
    }
  }

  // ── drawing on the page ─────────────────────────────────────────────
  _wireInk() {
    const cv = this.inkTarget
    const pos = e => { const b = cv.getBoundingClientRect(); return { x: (e.clientX - b.left) / b.width, y: (e.clientY - b.top) / b.height } }
    cv.addEventListener("pointerdown", e => { if (!this.drawing) return; e.preventDefault(); this.cur = [pos(e)]; this.strokes.push(this.cur); try { cv.setPointerCapture(e.pointerId) } catch (_) {} this._repaintInk() })
    cv.addEventListener("pointermove", e => { if (!this.drawing || !this.cur) return; e.preventDefault(); this.cur.push(pos(e)); this._repaintInk() })
    cv.addEventListener("pointerup", e => { if (!this.drawing || !this.cur) return; e.preventDefault(); this.cur.push(pos(e)); this.cur = null; this._repaintInk(); if (!this.recording) this._finishDraw() })
    cv.addEventListener("pointercancel", () => { this.cur = null })
  }

  _prepInk() {
    const cv = this.inkTarget, w = innerWidth, h = innerHeight, dpr = Math.min(2, devicePixelRatio || 1)
    cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr)
    cv.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0)
    this._repaintInk()
  }

  _paintStrokes(g, W, H, lw) {
    g.lineCap = "round"; g.lineJoin = "round"; g.strokeStyle = "#DC2626"; g.fillStyle = "#DC2626"; g.lineWidth = lw
    for (const st of this.strokes) {
      if (st.length < 2) { const p = st[0]; g.beginPath(); g.arc(p.x * W, p.y * H, lw / 2, 0, Math.PI * 2); g.fill(); continue }
      g.beginPath(); g.moveTo(st[0].x * W, st[0].y * H)
      for (let i = 1; i < st.length; i++) g.lineTo(st[i].x * W, st[i].y * H)
      g.stroke()
    }
  }

  _repaintInk() {
    const g = this.inkTarget.getContext("2d")
    g.clearRect(0, 0, innerWidth, innerHeight)
    this._paintStrokes(g, innerWidth, innerHeight, 4)
  }

  startDraw() {
    this.close("aside")
    this.state = "draw"
    this.strokes = []; this.cur = null; this.drawing = true
    this._prepInk()
    this.hintTarget.classList.remove("hidden")
    this.layerTarget.classList.remove("hidden", "pointer-events-none")
  }

  cancelDraw() {
    this.drawing = false; this.strokes = []
    this.layerTarget.classList.add("hidden")
    if (this._drawFor) return this._drawDone("instant-feedback:draw-cancelled", {})
    this.open(); this._setState("choose")
  }

  // A drawing made for someone else (Studio): hand the picture back and stay closed.
  _drawDone(name, detail) {
    const host = this._drawFor
    this._drawFor = null
    this.state = "closed"
    this.strokes = []
    window.dispatchEvent(new CustomEvent(name, { detail: { host, ...detail } }))
  }

  async _finishDraw() {
    this.drawing = false
    this.layerTarget.classList.add("hidden")
    const W = innerWidth, H = innerHeight
    let c = null
    try {
      c = await Promise.race([
        html2canvas(document.body, {
          x: scrollX, y: scrollY, width: W, height: H, scale: Math.min(1.5, devicePixelRatio || 1), useCORS: true, logging: false,
          ignoreElements: el => el === this.element || this.element.contains(el)
        }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("slow")), 7000))
      ])
      if (!c || !(c.width > 20 && c.height > 20)) c = null
    } catch (e) { c = null }
    if (!c) {
      this._toast(this.i18nValue.page_capture_failed, "alert")
      c = document.createElement("canvas"); c.width = W; c.height = H
      const g = c.getContext("2d"); g.fillStyle = "#F4F5F7"; g.fillRect(0, 0, W, H)
    }
    // html2canvas hands back a context that is still scaled and shifted —
    // reset it or the ink lands off the picture.
    const g = c.getContext("2d"); g.setTransform(1, 0, 0, 1, 0, 0)
    this._paintStrokes(g, c.width, c.height, Math.max(4, c.width / 220))
    const dataUrl = c.toDataURL("image/jpeg", 0.85)
    if (this._drawFor) return this._drawDone("instant-feedback:drawn", { dataUrl, width: c.width, height: c.height })
    const blob = await new Promise(r => c.toBlob(r, "image/jpeg", 0.85))
    this.form.shot = { blob, dataUrl }
    this.form.files = this.form.files.filter(f => f.role !== "screenshot")
    this.strokes = []
    this.open(); this._setState("shot")
  }

  // ── recording ───────────────────────────────────────────────────────
  async startRec() {
    const t = this.i18nValue
    if (!(navigator.mediaDevices?.getDisplayMedia && window.MediaRecorder)) { this._toast(t.not_supported, "alert"); return }
    const wantMic = !!this.bodyTarget.querySelector("[data-mic]")?.checked
    // The audio graph starts inside the click: a browser only lets an
    // AudioContext run when a user action starts it.
    let audio = this._audio = this._buildAudio()
    let display
    // The browser WINDOW, not just this tab (Phelipe 2026-09-24): a person opened an
    // attachment in a new tab mid-recording and it fell out of the video. Chrome and
    // Edge open the picker on Window; Firefox and Safari only ever offer windows or screens.
    // The picker names each window after the page showing in it, and a site can't pick
    // one for the person, so while it's open this page calls itself "Share this window"
    // (Phelipe 2026-09-24) — the easy one to spot. Its real name comes back right after.
    const pageTitle = document.title
    if (t.share_window_title) document.title = t.share_window_title
    try {
      display = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "window", frameRate: 15 }, audio: false,
        selfBrowserSurface: "include", surfaceSwitching: "exclude", systemAudio: "exclude" })
    } catch (e) {
      // They cancelled the share prompt - or the browser or the computer refused it (K13-28: that one tells the team).
      this._teardownAudio(audio)
      const refused = !/^(NotAllowedError|AbortError)$/.test(e?.name || "") || /system/i.test(e?.message || "")
      this._step(refused ? "share_failed" : "share_cancelled", `${e?.name || ""} ${e?.message || ""}`.trim())
      if (refused) this._reportIssue("record", `${e?.name || "Error"}: ${e?.message || "the screen share didn't start"}`)
      return
    }
    finally { document.title = pageTitle }
    let mic = null
    if (wantMic) { try { mic = await navigator.mediaDevices.getUserMedia(this._micConstraints()) } catch (e) { mic = null } }
    // Hidden-tab safety (2026-10-10): the voice must never depend on this tab
    // staying in front. A graph the browser won't start gets one more nudge;
    // if it still won't run, the mic goes straight into the recorder (no
    // switching or mute for that one recording, but the voice is there).
    if (audio && mic && AUDIO_STUCK.has(audio.ac.state)) {
      try { await Promise.race([audio.ac.resume(), new Promise(r => setTimeout(r, 600))]) } catch (_) {}
      if (AUDIO_STUCK.has(audio.ac.state)) { this._teardownAudio(audio); audio = null }
    }
    // With the graph, the recorder records the graph's own audio track. It
    // never changes, so the person can switch microphones or mute while
    // recording (a MediaRecorder gives up if its tracks change). Without
    // Web Audio the mic goes straight in, as before.
    let audioTracks
    if (audio) { if (mic) this._plugMic(mic); audioTracks = audio.dest.stream.getAudioTracks() }
    else audioTracks = mic ? mic.getAudioTracks() : []
    const tracks = [...display.getVideoTracks(), ...audioTracks]
    const stream = new MediaStream(tracks)
    const mime = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find(m => MediaRecorder.isTypeSupported(m)) || ""
    let rec
    try { rec = new MediaRecorder(stream, { mimeType: mime || undefined, videoBitsPerSecond: 900_000, audioBitsPerSecond: 64_000 }) }
    catch (e) {
      tracks.forEach(x => x.stop()); this._teardownAudio(audio); this._toast(t.not_supported, "alert")
      this._reportIssue("record", `MediaRecorder: ${e?.message || e}`)
      return
    }
    // One session per recording, with its own pieces, finished exactly once
    // (K3-43): every way a recording ends — Stop on the bar, "Stop sharing" in
    // the browser, the 3-minute limit, or the browser ending it by itself —
    // runs the same upload → AI write-up → fill the form. Before, a recorder
    // the browser had already ended got "finished" twice, and the second,
    // empty copy replaced the real one, so nothing was ever written up.
    const session = { sid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, chunks: [], seconds: 0, done: false }
    session.finish = () => {
      if (session.done) return
      session.done = true
      session.releaseLock?.()
      this._teardownAudio(audio) // let go only once the recorder has handed over its last piece
      this._recorded(stream, mime, session)
    }
    rec.ondataavailable = e => { if (e.data && e.data.size) { session.chunks.push(e.data); this._backupChunk(session, e.data) } }
    rec.onstop = () => {
      this._step("stopped", `${session.why || "by the browser"} after ${session.seconds}s`)
      if (!session.why && session.seconds < MAX_SECONDS) this._reportIssue("recording", `The recording stopped by itself after ${session.seconds}s`)
      if (this._recSession === session && this.recording) this._endRecUi()
      session.finish()
    }
    rec.onerror = (e) => {
      this._step("recorder_error", e?.error?.name || "error")
      setTimeout(() => { if (rec.state === "inactive") rec.onstop() }, 0)
    }
    display.getVideoTracks()[0].onended = () => { if (this._recSession === session) { session.why ||= "Stop sharing"; this.stopRec() } } // "Stop sharing" in the browser bar
    // Which window they shared (the same window, another one, the whole screen) - for the log.
    this._surface = display.getVideoTracks()[0]?.getSettings?.()?.displaySurface || ""
    this._step("recording", `${this._surface || "?"}${mic ? " + mic" : ""} on ${location.pathname}`)
    this.recorder = rec; this.recStream = stream; this._recSession = session; this.form.recMic = !!mic || !!this.form.recMic
    this.recording = true; this.recSeconds = 0
    // While recording, the graph is woken up again whenever the browser pauses
    // it — another tab or program in front, a headset plugged in — so the
    // recorder never waits on a silent track (Keane, Chrome on Windows).
    if (audio) {
      const wake = () => { if (this._audio === audio && AUDIO_STUCK.has(audio.ac.state)) audio.ac.resume?.().catch(() => {}) }
      audio.wake = wake
      audio.ac.onstatechange = wake
      this._onRecVisibility = wake
      document.addEventListener("visibilitychange", wake)
    }
    this.close("aside"); this.state = "recording"
    this.strokes = []; this.cur = null; this._prepInk(); this._setRecDraw(false)
    this.pillTarget.classList.remove("hidden"); this.pillTarget.classList.add("flex")
    this.haloTarget.classList.remove("hidden")
    this.timerTarget.textContent = "0:00"; this.timerTarget.classList.remove("text-amber-300")
    // Counted from the clock, not from the ticks: a browser slows the ticks of a
    // tab that isn't in front, and the time and the 3-minute stop must stay right.
    const startedAt = Date.now(), warned = {}
    this.recTimer = setInterval(() => {
      this.recSeconds = Math.max(this.recSeconds, Math.floor((Date.now() - startedAt) / 1000))
      session.seconds = this.recSeconds; this.timerTarget.textContent = this._fmt(this.recSeconds)
      audio?.wake?.()
      // Heads-up before the 3-minute stop (Phelipe 2026-10-10): a warning at 1 minute left, again at 10 seconds,
      // and the timer turns amber, so nobody is cut off mid-sentence. The stop at 3:00 still writes it up as usual.
      const left = MAX_SECONDS - this.recSeconds, t = this.i18nValue
      if (left <= 60 && left > 10 && !warned.minute) { warned.minute = true; this.timerTarget.classList.add("text-amber-300"); this._toast(t.recording_one_minute_left || "1 minute left — the recording stops by itself at 3:00 and is written up for you.") }
      if (left <= 10 && left > 0 && !warned.ten) { warned.ten = true; this._toast(t.recording_ten_seconds_left || "10 seconds left.") }
      if (this.recSeconds >= MAX_SECONDS) { session.why ||= "3:00"; this.stopRec() }
    }, 1000)
    this._paintMicButton()
    this._startWave()
    this._backupStart(session, mime)
    rec.start(1000)
    // Record from the Feedback page (K13-28): straight back to the page they came from, still recording.
    const back = this._onFeedbackPage() && this._session(PREV_PAGE)
    if (back && window.Turbo?.visit) { this._step("back_to_page", back); window.Turbo.visit(back) }
  }

  toggleRecDraw() { this._setRecDraw(!this.recDraw) }

  _setRecDraw(on) {
    this.recDraw = on; this.drawing = on
    this.drawBtnTarget.dataset.on = String(on)
    this.hintTarget.classList.add("hidden")
    if (on) { this.layerTarget.classList.remove("hidden", "pointer-events-none") }
    else { this.layerTarget.classList.add("pointer-events-none"); if (!this.strokes.length) this.layerTarget.classList.add("hidden") }
  }

  eraseAll() { this.strokes = []; this.cur = null; this._repaintInk(); if (!this.recDraw) this.layerTarget.classList.add("hidden") }

  // ── microphone while recording (Phelipe 2026-09-23) ─────────────────
  // The mic runs through a small Web Audio graph: mic → gain → the
  // recorder's audio track, with an analyser tapped off the gain. The
  // analyser draws the sound wave in the pill, the gain is the mute, and
  // switching microphones only swaps the graph's source — never the track
  // the recorder is writing.
  _buildAudio() {
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return null
    try {
      const ac = new AC()
      const gain = ac.createGain()
      const analyser = ac.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.55
      const dest = ac.createMediaStreamDestination()
      gain.connect(dest)
      gain.connect(analyser)
      if (ac.state === "suspended") ac.resume().catch(() => {})
      return { ac, gain, analyser, dest, src: null, mic: null, deviceId: null, muted: false }
    } catch (e) { return null }
  }

  _micConstraints(deviceId) {
    const audio = { echoCancellation: true, noiseSuppression: true }
    if (deviceId) audio.deviceId = { exact: deviceId }
    return { audio }
  }

  _plugMic(stream) {
    const a = this._audio
    if (!a) { stream.getTracks().forEach(x => x.stop()); return }
    try { a.src?.disconnect() } catch (_) {}
    a.mic?.getTracks().forEach(x => x.stop())
    a.mic = stream
    a.src = a.ac.createMediaStreamSource(stream)
    a.src.connect(a.gain)
    const track = stream.getAudioTracks()[0]
    a.deviceId = track?.getSettings?.().deviceId || null
    this.form.recMic = true // the AI listens to this recording
    // Unplugged mid-recording: the button says so instead of going quiet.
    if (track) track.onended = () => {
      if (this._audio !== a || a.mic !== stream) return
      try { a.src?.disconnect() } catch (_) {}
      a.src = null; a.mic = null
      this._paintMicButton()
      if (this.hasMicMenuTarget && !this.micMenuTarget.classList.contains("hidden")) this._renderMicMenu()
    }
  }

  // Stops the microphone (the browser's mic light goes off) and closes the
  // graph. Only clears the current graph when it's the one passed in, so a
  // late recorder stop can't take down a newer recording's audio.
  _teardownAudio(a = this._audio) {
    if (this._audio === a) { this._audio = null; cancelAnimationFrame(this._waveRaf); this._closeMicMenu() }
    if (!a) return
    if (this._onRecVisibility === a.wake && a.wake) { document.removeEventListener("visibilitychange", a.wake); this._onRecVisibility = null }
    a.wake = null
    try { a.ac.onstatechange = null } catch (_) {}
    try { a.src?.disconnect() } catch (_) {}
    a.mic?.getTracks().forEach(x => x.stop())
    try { a.ac.close() } catch (_) {}
  }

  async toggleMicMenu(e) {
    e?.stopPropagation()
    if (!this.hasMicMenuTarget) return
    if (!this.micMenuTarget.classList.contains("hidden")) { this._closeMicMenu(); return }
    await this._renderMicMenu()
    this.micMenuTarget.classList.remove("hidden")
    this._onMicOutside = (ev) => {
      if (this.micMenuTarget.contains(ev.target) || this.micBtnTarget.contains(ev.target)) return
      this._closeMicMenu()
    }
    document.addEventListener("pointerdown", this._onMicOutside, true)
  }

  _closeMicMenu() {
    if (this.hasMicMenuTarget) this.micMenuTarget.classList.add("hidden")
    if (this._onMicOutside) { document.removeEventListener("pointerdown", this._onMicOutside, true); this._onMicOutside = null }
  }

  async _renderMicMenu() {
    if (!this.hasMicMenuTarget) return
    const t = this.i18nValue, a = this._audio
    let mics = []
    try { mics = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "audioinput") } catch (_) { mics = [] }
    const live = !!(a && a.src)
    const rows = mics.map((d, i) => {
      const on = live && d.deviceId === a.deviceId
      const label = d.label || `${t.mic_n || "Microphone"} ${i + 1}`
      return `<button type="button" data-mic-id="${this._esc(d.deviceId)}" class="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-left hover:bg-gray-50 cursor-pointer">` +
             `<span class="w-4 shrink-0 text-brand font-bold">${on ? "✓" : ""}</span><span class="truncate">${this._esc(label)}</span></button>`
    }).join("")
    const mute = live
      ? `<div class="border-t border-gray-100 my-1"></div>` +
        `<button type="button" data-mic-mute class="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-left hover:bg-gray-50 cursor-pointer ${a.muted ? "text-brand font-semibold" : "text-gray-700"}">` +
        `<span class="w-4 shrink-0"></span>${this._esc(a.muted ? (t.mic_unmute || "Unmute microphone") : (t.mic_mute || "Mute microphone"))}</button>`
      : ""
    const note = (!live || a.muted) ? `<p class="px-3 pt-1 pb-1.5 text-[13px] text-red-600">${this._esc(t.mic_off_note || "Your voice isn't being recorded")}</p>` : ""
    this.micMenuTarget.innerHTML =
      `<p class="px-3 pt-1.5 pb-1 text-[13px] font-semibold text-gray-500">${this._esc(t.mic_heading || "Microphone")}</p>` +
      (rows || `<p class="px-3 py-1.5 text-[13px] text-gray-500">${this._esc(t.mic_none || "No microphone found")}</p>`) + mute + note
    this.micMenuTarget.querySelectorAll("[data-mic-id]").forEach(b => { b.onclick = () => this.pickMic(b.dataset.micId) })
    const m = this.micMenuTarget.querySelector("[data-mic-mute]")
    if (m) m.onclick = () => this.toggleMicMute()
  }

  async pickMic(deviceId) {
    const a = this._audio
    if (!a) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia(this._micConstraints(deviceId))
      if (this._audio !== a) { stream.getTracks().forEach(x => x.stop()); return } // recording ended meanwhile
      this._plugMic(stream)
      if (a.muted) { a.muted = false; a.gain.gain.value = 1 } // picking a mic means "use it"
    } catch (e) {
      this._toast(this.i18nValue.mic_failed || "Couldn't use that microphone", "alert")
    }
    this._paintMicButton()
    await this._renderMicMenu()
  }

  toggleMicMute() {
    const a = this._audio
    if (!a || !a.src) return
    a.muted = !a.muted
    a.gain.gain.value = a.muted ? 0 : 1
    this._paintMicButton()
    this._renderMicMenu()
  }

  // Button + wave only exist with the audio graph; without Web Audio the
  // recording still works, it just can't be switched or shown.
  _paintMicButton() {
    if (!this.hasMicBtnTarget) return
    const a = this._audio
    this.micBtnTarget.classList.toggle("hidden", !a)
    if (this.hasWaveTarget) this.waveTarget.classList.toggle("hidden", !a)
    if (!a) return
    const live = !!(a.src && !a.muted)
    this.micBtnTarget.dataset.live = String(live)
    this.micBtnTarget.querySelector("[data-mic-slash]")?.classList.toggle("hidden", live)
  }

  // Bars that move with the voice: green while it hears you, grey and flat
  // while it's quiet, muted, or there's no microphone.
  _startWave() {
    if (!this.hasWaveTarget || !this._audio) return
    const a = this._audio, cv = this.waveTarget
    const g = cv.getContext?.("2d")
    if (!g) return
    const W = 44, H = 20, BARS = 9, BW = 3, GAP = (W - BARS * BW) / (BARS - 1)
    const dpr = window.devicePixelRatio || 1
    cv.width = W * dpr; cv.height = H * dpr
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    const bins = new Uint8Array(a.analyser.frequencyBinCount)
    const span = Math.min(bins.length, 48) // speech sits in the low bins
    const draw = () => {
      if (!this.recording || this._audio !== a) return
      const levels = []
      if (a.src && !a.muted) {
        a.analyser.getByteFrequencyData(bins)
        for (let i = 0; i < BARS; i++) {
          const from = Math.floor(i * span / BARS), to = Math.max(from + 1, Math.floor((i + 1) * span / BARS))
          let sum = 0
          for (let j = from; j < to; j++) sum += bins[j]
          levels.push(sum / (to - from) / 255)
        }
      }
      const talking = levels.some(v => v > 0.12)
      g.clearRect(0, 0, W, H)
      g.fillStyle = talking ? "#34d399" : "rgba(249, 250, 251, 0.35)"
      for (let i = 0; i < BARS; i++) {
        const h = Math.max(2, Math.min(H, (levels[i] || 0) * H * 1.6))
        g.fillRect(i * (BW + GAP), (H - h) / 2, BW, h)
      }
      this._waveRaf = requestAnimationFrame(draw)
    }
    cancelAnimationFrame(this._waveRaf)
    this._waveRaf = requestAnimationFrame(draw)
  }

  stopRec() {
    if (!this.recording) return
    this._endRecUi()
    const r = this.recorder, s = this._recSession
    if (s) s.why ||= "Stop"
    // The recorder hands over its last piece, then its onstop finishes the session.
    try { if (r && r.state !== "inactive") { r.stop(); return } } catch (_) { /* finished below */ }
    s?.finish()
  }

  // Takes the recording bar, ring and ink away. The recording itself is finished by its session.
  _endRecUi() {
    this.recording = false
    clearInterval(this.recTimer)
    this.pillTarget.classList.add("hidden"); this.pillTarget.classList.remove("flex")
    this.haloTarget.classList.add("hidden")
    this.drawing = false; this.recDraw = false; this.strokes = []; this.cur = null
    this.layerTarget.classList.add("hidden"); this.layerTarget.classList.remove("pointer-events-none")
    cancelAnimationFrame(this._waveRaf); this._closeMicMenu()
  }

  async _recorded(stream, mime, session) {
    stream?.getTracks().forEach(x => x.stop())
    const blob = new Blob(session?.chunks || [], { type: mime || "video/webm" })
    if (session) session.chunks = []
    this._dropBackup(session?.sid)
    if (!blob.size) {
      // Nothing came through (the share ended right away): back to Start, never an empty video.
      const t = this.i18nValue
      this._toast(t.recording_empty || "The recording came out empty. Please try again.", "alert")
      this.open()
      if (this.state === "recording") this._setState(this.form.rec ? "rec" : "recpre")
      return
    }
    return this._useRecording(blob, session?.seconds ?? this.recSeconds)
  }

  // A finished recording (or one saved before a page reload) → the form, then
  // upload → AI write-up → title and description filled in.
  async _useRecording(blob, len, { cut = false } = {}) {
    if (this.form.rec?.url) URL.revokeObjectURL(this.form.rec.url)
    const ext = /mp4/.test(blob.type) ? "mp4" : "webm"
    const t = this.i18nValue
    const rec = { blob, url: URL.createObjectURL(blob), len, name: `recording-${Date.now()}.${ext}`, type: blob.type, mic: !!this.form.recMic }
    this.form.rec = rec
    this.form.recStatus = t.uploading_recording || t.uploading; this.form.recBusy = true
    this.open(); this._setState("rec")
    // Upload now so the AI can listen, and so Send is instant. The entry
    // keeps its promise: Send may come before the upload is through.
    const entry = { name: rec.name, size: blob.size, type: blob.type, role: "recording", status: "uploading" }
    rec.entry = entry
    entry.promise = this._upload(entry, blob)
    this.form.files = this.form.files.filter(f => f.role !== "recording").concat(entry)
    this._renderChips()
    // Watched (K13-28): an upload past 2 minutes tells the team, and the person sees why instead of a spinner.
    this._step("upload", `${Math.round(blob.size / 1e5) / 10} MB`)
    let stop = this._watch("upload", WATCH_MS.upload, () => this._listening(rec, t.slow_told || SLOW_TOLD, true))
    const ok = await entry.promise
    stop()
    this._renderChips()
    this._step(ok ? "uploaded" : "upload_failed", entry.error || "")
    if (!ok) { rec.summary = null; this._listening(rec, t.summary_failed, true); this._reportIssue("upload", entry.error || "upload failed"); return }
    // A recording a page load cut short (the browser kept what came before): the team hears, with the recording.
    if (cut) this._reportIssue("recording", `A full page load stopped the recording after ${len}s`)
    // An answer on a request (2026-10-09): no write-up to wait for — the recording is the answer.
    if (this.commentFor) { rec.summary = null; this._listening(rec, t.comment_rec_ready || "Recording ready — add a few words if you like, then send.", true); return }
    this._listening(rec, rec.mic ? t.listening : t.watching)
    // The AI reviews the video and the words. If the person already hit
    // Send, the write-up goes onto the saved request instead of the form.
    stop = this._watch("ai", WATCH_MS.ai, () => this._listening(rec, t.slow_told || SLOW_TOLD, true))
    const s = await this._summarize(entry, blob.type)
    stop()
    this._step(s?.summary ? "ai_done" : "ai_failed")
    if (!s?.summary) this._reportIssue("ai", "no write-up came back")
    // No paragraph back (failed, too slow, or empty) while the video is safely up:
    // null tells Send to have the server write it up right away (`now`), and
    // a request already sent gets it from the server's own write-up.
    if (!s || !s.summary) { rec.summary = null; this._listening(rec, t.summary_later || t.summary_failed, true); return }
    rec.summary = s
    if (this.form.rec === rec) {
      this._applyAi({ title: s.title, desc: s.summary, kind: s.kind, priority: s.priority, why: s.why }, true)
      this._listening(rec, rec.mic ? t.summarized_voice : t.summarized_screen, true)
    } else if (rec.sent) {
      this._summaryLate(rec, s)
    }
  }

  // Status strip on the recording — only while that recording's form is up.
  _listening(rec, text, done = false) {
    if (this.form.rec !== rec) return
    this.form.recStatus = text; this.form.recBusy = !done
    // Record again + this pill share one row (Phelipe 2026-10-17): the pill's text
    // shortens with an ellipsis instead of wrapping under the button.
    const el = this.bodyTarget.querySelector("[data-listening]"); if (el) el.innerHTML = `<span class="shrink-0 inline-flex">${this._icon("spark")}</span><span class="truncate">${this._esc(text)}</span>`
    const strip = this.bodyTarget.querySelector("[data-recstatus]"), txt = this.bodyTarget.querySelector("[data-recstatus-text]"), spin = this.bodyTarget.querySelector("[data-recspin]")
    if (txt) txt.textContent = text
    if (spin) spin.classList.toggle("hidden", done)
    if (strip) { strip.classList.remove("hidden"); if (done) setTimeout(() => { if (this.form.rec === rec && this.form.recBusy === false) strip.classList.add("hidden") }, 4000) }
    this._paintSend()
  }

  // Asks the server for the write-up, twice if need be (Phelipe 2026-09-24:
  // "sometimes the narration comes, sometimes it doesn't"). The server
  // already retries a busy Google; this covers the trip between here and
  // there. A recording too big to send at all isn't worth a second try.
  // Each ask gives up after SUMMARIZE_TIMEOUT_MS (the server's own limit is
  // 60 s), so a stuck answer never leaves the form waiting forever — the
  // server's write-up takes over. A tab in the background asks all the same.
  async _summarize(entry, type) {
    let titleOnly = null
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt) await new Promise(r => setTimeout(r, this._summarizeRetryMs ?? 2500))
      const ctrl = typeof AbortController === "function" ? new AbortController() : null
      let timer
      try {
        const slow = new Promise((_, rej) => { timer = setTimeout(() => { ctrl?.abort(); rej(new Error("timeout")) }, this._summarizeTimeoutMs ?? SUMMARIZE_TIMEOUT_MS) })
        const ask = (async () => {
          const res = await fetch(this.summarizeUrlValue, { method: "POST", headers: this._headers(), signal: ctrl?.signal,
            body: JSON.stringify({ cache_name: entry.cache_name, content_type: type, context: this._context() }) })
          return res.ok ? await res.json() : null
        })()
        ask.catch(() => {})
        const data = await Promise.race([ask, slow])
        const s = data?.suggestion
        if (s?.summary) return s
        if (s?.title) titleOnly = s
        if (data?.too_big) break
      } catch (e) { /* dropped connection or too slow: once more */ }
      finally { clearTimeout(timer) }
    }
    return titleOnly
  }

  // A recording whose AI write-up isn't back yet (Phelipe 2026-09-24): Send
  // still goes, and the write-up lands on the saved request when it's ready.
  // undefined = the AI is still on it; null = the panel's own try failed, so
  // the server writes it up the moment the request is sent.
  _aiPending() {
    const r = this.form.rec
    return !!r && r.summary == null && r.entry?.status !== "failed"
  }

  // "Send · AI summary after upload" while the video uploads, "… follows"
  // while the AI watches it, plain Send once the form is filled in.
  _sendLabel() {
    const t = this.i18nValue
    if (!this._aiPending()) return t.send
    return (this.form.rec.entry?.status === "uploading" ? t.send_ai_upload : t.send_ai_watch) || t.send
  }

  _paintSend() {
    const btn = this.bodyTarget.querySelector("[data-act=send]")
    if (btn && !btn.disabled) btn.textContent = this._sendLabel()
  }

  _fmt(s) { return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` }

  // ── AI suggestions ──────────────────────────────────────────────────
  _queueSuggest() {
    clearTimeout(this._suggestT)
    if (this.form.aiFilled) return
    const text = this.form.desc.trim()
    if (text.length < 8) return
    this._suggestT = setTimeout(async () => {
      if (text !== this.form.desc.trim()) return
      try {
        const res = await fetch(this.suggestUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify({ text, context: this._context(), draft_id: this.form.draftId }) })
        const data = res.ok ? await res.json() : null
        if (data?.suggestion && text === this.form.desc.trim()) this._applyAi(data.suggestion, false)
        // The same call said whether the company already sent this one (K9-5): Send uses it for these words.
        if (data?.similar_checked && text === this.form.desc.trim()) this.form.similar = { key: text, match: data.similar || null }
      } catch (e) { /* the person keeps typing */ }
    }, 900)
  }

  _applyAi(s, fromRecording) {
    const b = this.bodyTarget
    if (fromRecording && s.desc) { this.form.desc = s.desc; const d = b.querySelector("[data-field=desc]"); if (d) d.value = s.desc; this.form.aiFilled = true; this._showAi("desc") }
    if (s.title && !this.form.titleTouched) { this.form.title = s.title; const el = b.querySelector("[data-field=title]"); if (el) el.value = s.title; this._showAi("title") }
    if (s.kind && KINDS.includes(s.kind) && !this.form.kindTouched) { this.form.kind = s.kind; b.querySelectorAll("[data-opt=kind]").forEach(x => x.dataset.on = String(x.dataset.v === s.kind)); this._showAi("kind") }
    if (s.priority && PRIOS.includes(s.priority) && !this.form.prioTouched) { this.form.prio = s.priority; b.querySelectorAll("[data-opt=prio]").forEach(x => x.dataset.on = String(x.dataset.v === s.priority)); this._showAi("prio") }
    if (s.why) { this.form.why = s.why; this._showWhy(s.why) }
  }

  _showAi(id) { const el = this.bodyTarget.querySelector(`[data-ai=${id}]`); if (el) el.hidden = false }
  _hideAi(id) { const el = this.bodyTarget.querySelector(`[data-ai=${id}]`); if (el) el.hidden = true }
  // The AI's one-line "why" under the priority, and the "We'll include where
  // you are" box (in _formHtml), stay out of the chat window (Phelipe
  // 2026-10-17: "hide both of these in the window chat. in the pop up it's ok").
  _showWhy(text) { if (this._host) return; const w = this.bodyTarget.querySelector("[data-why]"); if (w) { w.textContent = text; w.classList.remove("hidden") } }

  // ── files ───────────────────────────────────────────────────────────
  _wireDrop() {
    const p = this.panelTarget
    p.addEventListener("dragover", e => { if (!this.bodyTarget.querySelector("[data-drop]")) return; e.preventDefault(); p.classList.add("ring-2", "ring-brand", "ring-inset") })
    p.addEventListener("dragleave", e => { if (!p.contains(e.relatedTarget)) p.classList.remove("ring-2", "ring-brand", "ring-inset") })
    p.addEventListener("drop", e => { if (!this.bodyTarget.querySelector("[data-drop]")) return; e.preventDefault(); p.classList.remove("ring-2", "ring-brand", "ring-inset"); this._addFiles(e.dataTransfer.files) })
  }

  async _addFiles(list) {
    const files = [...(list || [])]
    if (!files.length) return
    for (const f of files) {
      const entry = { name: f.name, size: f.size, type: f.type, role: "file", status: "uploading" }
      this.form.files.push(entry)
      this._renderChips()
      entry.promise = this._upload(entry, f)
      entry.promise.then(() => this._renderChips())
    }
  }

  async _upload(entry, blob) {
    this._uploadsInFlight = (this._uploadsInFlight || 0) + 1
    try {
      const pre = await fetch(this.presignUrlValue, { method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: entry.name, content_type: entry.type || "application/octet-stream", byte_size: entry.size }] }) })
      if (!pre.ok) throw new Error((await pre.json().catch(() => ({}))).error || "presign")
      const up = (await pre.json()).uploads[0]
      const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: blob })
      if (!put.ok) throw new Error("put")
      entry.cache_name = up.cache_name; entry.status = "done"
      return true
    } catch (e) {
      entry.status = "failed"
      entry.error = e?.message || "failed"
      this._toast(e.message && e.message !== "presign" && e.message !== "put" ? e.message : this.i18nValue.upload_failed, "alert")
      return false
    } finally {
      this._uploadsInFlight--
    }
  }

  _renderChips() {
    const box = this.bodyTarget.querySelector("[data-chips]")
    if (!box) return
    const t = this.i18nValue
    box.innerHTML = this.form.files.map((f, i) => {
      const up = f.status === "uploading", bad = f.status === "failed"
      return `<span class="inline-flex items-center gap-1.5 max-w-full rounded-full border px-2.5 py-0.5 text-[12.5px] font-medium ${up ? "bg-blue-50 text-blue-800 border-blue-200" : bad ? "bg-red-50 text-red-800 border-red-200" : "bg-gray-100 text-gray-700 border-gray-200"}">
        ${up ? `<span class="w-3 h-3 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin"></span>` : this._icon("file")}
        <span class="truncate max-w-[170px]">${this._esc(f.name)}</span>${f.status === "attached" ? "" : `<small class="opacity-70">${this._size(f.size)}</small>`}
        ${up ? `<small>${this._esc(t.uploading)}</small>` : `<button type="button" class="px-1 leading-none opacity-70 hover:opacity-100 cursor-pointer" data-rm="${i}" aria-label="Remove">×</button>`}</span>`
    }).join("")
    box.querySelectorAll("[data-rm]").forEach(b => b.onclick = () => { const f = this.form.files[+b.dataset.rm]; if (f?.status === "attached" && f.attachmentId) this.form.removeIds.push(f.attachmentId); if (f?.role === "recording" && this.form.rec) { URL.revokeObjectURL(this.form.rec.url); this.form.rec = null } this.form.files.splice(+b.dataset.rm, 1); this._renderChips() })
    this._paintSend()
  }

  _size(b) { return b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB` }

  // ── send ────────────────────────────────────────────────────────────
  async submit() {
    if (this.commentFor) return this._submitComment()
    const t = this.i18nValue, b = this.bodyTarget, btn = b.querySelector("[data-act=send]"), err = b.querySelector("[data-err]")
    const desc = this.form.desc.trim(), title = this.form.title.trim()
    // A recording on its own is enough (2026-09-24): the AI writes the title and summary.
    const hasRec = !!this.form.rec && this.form.rec.entry?.status !== "failed"
    if (!desc && !title && !hasRec) { err.textContent = t.describe; err.classList.remove("hidden"); b.querySelector("[data-field=desc]")?.focus(); return }
    err.classList.add("hidden")
    btn.disabled = true
    // Already on the list? (Kloepfer K9-5): one like it gets a notice above Send
    // instead — add it there as a comment, or send it as new. Never holds Send
    // for more than 3 s; a check that fails or is slow just sends.
    const words = this._dupText()
    const dismissed = !!words && this.form.dupSkip === words
    if (!dismissed) {
      const match = await this._duplicateCheck(btn, words)
      if (match) { btn.disabled = false; btn.textContent = this._sendLabel(); return this._showDuplicate(match, words) }
    }
    btn.textContent = t.sending
    this._step("send")
    const stop = this._watch("send", WATCH_MS.send, () => { err.textContent = t.slow_told || SLOW_TOLD; err.classList.remove("hidden") })
    try {
      await this._uploadShot()
      // A recording (or a big file) still uploading doesn't hold the Send:
      // the request is saved now and shows "still loading" on the list; the
      // file is attached the moment it lands. This element outlives page
      // changes, so the upload keeps going wherever the person goes next.
      const late = this.form.files.filter(f => f.status === "uploading")
      // A saved draft is sent in place (draft: false); anything else is new.
      const draftId = this.form.draftId
      const res = await fetch(draftId ? `${this.createUrlValue}/${draftId}` : this.createUrlValue, { method: draftId ? "PATCH" : "POST", headers: this._headers(),
        body: JSON.stringify({ ...this._payload(late), ...(draftId ? { draft: false } : {}), ...(dismissed ? { duplicate_dismissed: true } : {}) }) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || t.upload_failed)
      const id = data.request?.id
      this._announce(data.request, false)
      this.lastId = id
      this.lastPublicUrl = data.request?.public_url || ""
      // Review before ConsLog (K8-31): waiting for one of the company's reviewers.
      this.lastReview = data.request?.review_state === "pending"
      const rec = this.form.rec
      // The AI is still watching the recording: its write-up lands on the saved request.
      if (rec && rec.summary === undefined && rec.entry?.status !== "failed") rec.sent = { id, kindTouched: this.form.kindTouched, prioTouched: this.form.prioTouched, titleTouched: this.form.titleTouched }
      // The sent screen says the write-up follows — from here, or from the server if this try failed.
      this.aiLater = this._aiPending()
      if (rec?.url) URL.revokeObjectURL(rec.url)
      this.late = late.length ? { id, entries: late } : null
      late.forEach(e => e.promise?.then(ok => this._attachLate(id, e, ok)))
      this._placeRow(data.row_html, id)
      stop()
      this._step("sent", `#${id}`)
      this.form = this._blankForm()
      this._setState("sent")
    } catch (e) {
      stop()
      this._step("send_failed", e.message)
      this._reportIssue("send", e.message || "Send failed")
      err.textContent = e.message || t.upload_failed; err.classList.remove("hidden")
      btn.disabled = false; btn.textContent = this._sendLabel()
    }
  }

  // An answer on a request (Phelipe 2026-10-09): the words and the files — the recording
  // too, once it's up — go onto the request as a comment; the drawer, if open, shows it.
  async _submitComment() {
    const t = this.i18nValue, b = this.bodyTarget, btn = b.querySelector("[data-act=send]"), err = b.querySelector("[data-err]")
    const desc = this.form.desc.trim()
    const hasRec = !!this.form.rec && this.form.rec.entry?.status !== "failed"
    if (!desc && !hasRec && !this.form.files.length && !this.form.shot) { err.textContent = t.describe; err.classList.remove("hidden"); b.querySelector("[data-field=desc]")?.focus(); return }
    err.classList.add("hidden")
    btn.disabled = true; btn.textContent = t.sending
    try {
      await this._uploadShot()
      await Promise.all(this.form.files.filter(f => f.status === "uploading").map(f => f.promise))
      const files = this.form.files.filter(f => f.status === "done").map(f => ({ cache_name: f.cache_name, filename: f.name, content_type: f.type, byte_size: f.size, role: f.role }))
      const id = this.commentFor.id
      const res = await fetch(`${this.createUrlValue}/${id}/comments`, { method: "POST", headers: this._headers(), body: JSON.stringify({ comment: { body: desc, files } }) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || t.upload_failed)
      window.dispatchEvent(new CustomEvent("feedback:comment-added", { detail: { id, comment: data.comment, comment_count: data.comment_count, question_pending: data.question_pending } }))
      this.lastId = id; this.lastPublicUrl = ""; this.lastReview = false; this.lastComment = true
      if (this.form.rec?.url) URL.revokeObjectURL(this.form.rec.url)
      this.form = this._blankForm(); this.commentFor = null
      this._setState("sent")
    } catch (e) {
      err.textContent = e.message || t.upload_failed; err.classList.remove("hidden")
      btn.disabled = false; btn.textContent = t.comment_send || "Send answer"
    }
  }

  // ── Already on the list? (Kloepfer K9-5, 2026-10-16) ────────────────
  // Keane held a list back because many items were already being worked on
  // and he feared a second copy would confuse things. Before a new request
  // goes out, the company's own requests that look like it are checked: the
  // typing step's answer when it covers these words, otherwise one quick ask.
  _dupText() {
    const f = this.form
    return [f.title.trim(), f.desc.trim()].filter(Boolean).join("\n")
  }

  // The look-alike to offer, or null — never later than DUP_TIMEOUT_MS, never an error.
  async _duplicateCheck(btn, words) {
    if (!this.similarUrlValue || this.commentFor || (words || "").length < 8) return null
    const f = this.form, desc = f.desc.trim()
    if (desc && f.similar && f.similar.key === desc) return f.similar.match ? { ...f.similar.match, via: "typing" } : null
    if (btn) btn.textContent = this.i18nValue.dup_checking || "Checking for similar requests…"
    const ctrl = typeof AbortController === "function" ? new AbortController() : null
    let timer
    try {
      const slow = new Promise(resolve => { timer = setTimeout(() => { ctrl?.abort(); resolve(null) }, this._dupTimeoutMs ?? DUP_TIMEOUT_MS) })
      const ask = fetch(this.similarUrlValue, { method: "POST", headers: this._headers(), signal: ctrl?.signal,
        body: JSON.stringify({ text: words, draft_id: f.draftId, context: this._context() }) })
        .then(res => (res.ok ? res.json() : null))
        .then(d => (d?.similar?.id ? { ...d.similar, via: "send" } : null))
        .catch(() => null)
      return await Promise.race([ask, slow])
    } catch (_) {
      return null
    } finally {
      clearTimeout(timer)
    }
  }

  // The notice above Send (Phelipe 2026-10-19, same look as the phone's): "Already on the
  // list?", the matching request as a card they can open for a quick look (what was asked,
  // what we did), then the two choices stacked — add theirs as a comment there (a finished
  // one is reopened as well), or send it as new.
  //
  // A pop-up since K13-28 (Larry, Oct 27): under the pinned Send bar the notice
  // landed out of sight and Send looked like it did nothing. Over the panel it
  // can't be missed: add as a comment, send as new, or Cancel (or a click on
  // the backdrop) to go back to the form.
  _showDuplicate(match, words) {
    const t = this.i18nValue, esc = this._esc
    this.form.dupSkip = words // Send (or "Send as new") now sends these words as a new request
    this._hideDup()
    this._step("duplicate_shown", match.ref || `#${match.id}`)
    const modal = document.createElement("div")
    modal.className = "fixed inset-0 z-[2147483060] flex items-center justify-center bg-black/40 p-4"
    modal.setAttribute("role", "dialog")
    modal.setAttribute("aria-modal", "true")
    modal.dataset.dupModal = ""
    modal.addEventListener("click", (e) => { if (e.target === modal) this._cancelDup() })
    this.element.appendChild(modal)
    this._dupModal = modal
    const btn = "w-full rounded-lg font-semibold px-3 py-2 text-[13px] text-center cursor-pointer disabled:opacity-60"
    modal.innerHTML = `<div class="w-full max-w-md max-h-[90vh] overflow-auto rounded-xl bg-white shadow-2xl p-4 text-[13px] text-gray-800" data-dup-box data-dup-id="${+match.id}">
        <h3 class="m-0 text-[15px] font-bold text-gray-900">${esc(t.dup_title || "Already on the list?")}</h3>
        <p class="m-0 mt-1">${esc(t.dup_notice || "This looks like a request your team already sent. Add yours as a comment there instead?")}</p>
        <button type="button" class="mt-2 w-full flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-left cursor-pointer hover:bg-amber-100" data-dup-act="peek" aria-expanded="false">
          <span class="shrink-0 font-mono text-[13px] text-gray-500">${esc(match.ref || "")}</span>
          <span class="flex-1 min-w-0 truncate font-semibold text-gray-900">${esc(match.title || "")}</span>
          <span class="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[13px] text-gray-700">${esc(match.status_label || "")}</span>
          <span class="shrink-0 text-gray-400" data-dup-chev>›</span>
        </button>
        <p class="m-0 mt-1 text-amber-800">${esc(t.dup_see || "Click it to read what was asked and compare")}</p>
        <div class="hidden mt-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-gray-800" data-dup-peek></div>
        ${match.done ? `<p class="m-0 mt-2">${esc(t.dup_done_hint || "It's marked done. Adding yours there reopens it, so the team looks at it again.")}</p>` : ""}
        <p class="hidden m-0 mt-2 text-[13px] text-red-700" data-dup-err></p>
        <div class="flex flex-col gap-2 mt-3">
          <button type="button" class="${btn} bg-brand hover:bg-brand-dark text-white" data-dup-act="comment">${esc(match.done ? (t.dup_add_reopen || "Add this request detail as a comment and reopen it") : (t.dup_add_comment || "Add this request detail as a comment"))}</button>
          <button type="button" class="${btn} border border-gray-300 bg-white hover:bg-gray-50 text-gray-700" data-dup-act="new">${esc(t.dup_send_new || "Send as new")}</button>
          <button type="button" class="${btn} text-gray-500 hover:text-gray-800 hover:bg-gray-50" data-dup-act="cancel">${esc(t.dup_cancel || "Cancel")}</button>
        </div></div>`
    const box = modal.querySelector("[data-dup-box]")
    box.querySelector('[data-dup-act="comment"]').onclick = () => { this._step("duplicate_comment"); this._addAsComment(match) }
    box.querySelector('[data-dup-act="new"]').onclick = () => { this._step("duplicate_new"); this._hideDup(); this.submit() }
    box.querySelector('[data-dup-act="cancel"]').onclick = () => this._cancelDup()
    box.querySelector('[data-dup-act="peek"]').onclick = () => this._peekDuplicate(match)
    box.querySelector('[data-dup-act="comment"]').focus?.()
    // No words from the request: just which one, its state, and what came with it.
    track("Feedback Duplicate Suggested", { request_id: match.id, status: match.status, done: !!match.done, via: match.via || "send",
      from_draft: !!this.form.draftId, has_files: this.form.files.length > 0 || !!this.form.shot, has_recording: !!this.form.rec })
  }

  // The quick look, inside the notice: what was asked, what we did (when done), and
  // "Is yours the same thing?" — the two choices below it get the yes / no wording.
  async _peekDuplicate(match) {
    const t = this.i18nValue, esc = this._esc, box = this._dupModal?.querySelector("[data-dup-box]")
    const peek = box?.querySelector("[data-dup-peek]"), head = box?.querySelector('[data-dup-act="peek"]')
    if (!peek || !head) return
    const open = !peek.classList.toggle("hidden")
    head.setAttribute("aria-expanded", String(open))
    const chev = head.querySelector("[data-dup-chev]")
    if (chev) chev.textContent = open ? "⌄" : "›"
    if (!open) return
    if (!peek.dataset.loaded) {
      peek.innerHTML = `<p class="m-0 text-gray-500">…</p>`
      let r = null
      try {
        const res = await fetch(`${this.createUrlValue}/${match.id}`, { headers: { Accept: "application/json" } })
        r = res.ok ? (await res.json())?.request : null
      } catch (_) { r = null }
      const cut = (x) => (x.length > 900 ? `${x.slice(0, 900)}…` : x)
      const problem = cut((r?.problem || "").trim()), solution = cut((r?.solution || "").trim())
      const label = (x) => `<p class="m-0 text-[13px] font-bold uppercase tracking-wide text-gray-500">${esc(x)}</p>`
      peek.innerHTML = `${label(t.dup_what_asked || "What was asked")}
        <p class="m-0 mt-1 whitespace-pre-line">${esc(problem || r?.title || match.title || "")}</p>
        ${solution ? `<div class="mt-2">${label(t.dup_what_done || "What we did")}<p class="m-0 mt-1 whitespace-pre-line rounded-lg bg-emerald-50 text-emerald-800 px-2.5 py-1.5">${esc(solution)}</p></div>` : ""}
        <p class="m-0 mt-2 font-semibold text-gray-900">${esc(t.dup_same || "Is yours the same thing?")}</p>`
      peek.dataset.loaded = "1"
      track("Feedback Duplicate Peeked", { request_id: match.id, status: match.status, done: !!match.done })
    }
    const yes = box.querySelector('[data-dup-act="comment"]'), no = box.querySelector('[data-dup-act="new"]')
    if (yes && !match.done) yes.textContent = t.dup_yes || "Yes, add this request detail as a comment"
    if (no) no.textContent = t.dup_no || "No, send as new"
  }

  _hideDup() {
    this._dupModal?.remove()
    this._dupModal = null
  }

  // Back to the form; Send asks again if the words stay the same.
  _cancelDup() {
    this._step("duplicate_cancelled")
    this._hideDup()
    this.form.dupSkip = null
  }

  // "Add as a comment": their words — and every file, screenshot or recording
  // (comments carry files) — go on that request as a comment. A draft they were
  // finishing goes to the trash under the list. The panel closes with a toast.
  async _addAsComment(match) {
    const t = this.i18nValue, b = this.bodyTarget, box = this._dupModal?.querySelector("[data-dup-box]")
    const err = box?.querySelector("[data-dup-err]") || b.querySelector("[data-err]")
    const add = box?.querySelector('[data-dup-act="comment"]'), label = add?.textContent
    const btns = [...(box?.querySelectorAll("button") || []), b.querySelector("[data-act=send]"), b.querySelector("[data-act=draft]")].filter(Boolean)
    btns.forEach(x => { x.disabled = true })
    if (add) add.textContent = t.sending
    err?.classList.add("hidden")
    try {
      await this._uploadShot()
      await Promise.all(this.form.files.filter(f => f.status === "uploading").map(f => f.promise))
      const files = this.form.files.filter(f => f.status === "done").map(f => ({ cache_name: f.cache_name, filename: f.name, content_type: f.type, byte_size: f.size, role: f.role }))
      const draftId = this.form.draftId
      const res = await fetch(`${this.createUrlValue}/${match.id}/comments`, { method: "POST", headers: this._headers(),
        body: JSON.stringify({ comment: { body: this._commentBody(), files }, from_duplicate: true, ...(match.done ? { reopen: true } : {}),
                               ...(draftId ? { from_draft_id: draftId, remove_attachment_ids: this.form.removeIds || [] } : {}) }) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || t.upload_failed)
      window.dispatchEvent(new CustomEvent("feedback:comment-added", { detail: { id: match.id, comment: data.comment, comment_count: data.comment_count, question_pending: data.question_pending } }))
      if (draftId) this._dropRow(draftId, data.trash_html)
      if (data.reopened) this._requestUpdated(match.id) // the row's status pill and an open details panel follow
      if (this.form.rec?.url) URL.revokeObjectURL(this.form.rec.url)
      this._hideDup()
      this.form = this._blankForm()
      this.close()
      const said = data.reopened ? (t.dup_reopened || "Added to %{ref} as a comment and reopened it — thank you.") : (t.dup_added || "Added to %{ref} as a comment — thank you.")
      this._toast(said.replace("%{ref}", () => match.ref || match.title || ""))
    } catch (e) {
      if (err) { err.textContent = e.message || t.upload_failed; err.classList.remove("hidden") }
      btns.forEach(x => { x.disabled = false })
      if (add) add.textContent = label
    }
  }

  // The comment's words: what they wrote, led by a title they typed themselves when the text doesn't already say it.
  // After a reopen from the notice: the list row and an open details panel redraw from the request.
  async _requestUpdated(id) {
    try {
      const res = await fetch(`${this.createUrlValue}/${id}`, { headers: { Accept: "application/json" } })
      const data = res.ok ? await res.json() : null
      if (data?.request) window.dispatchEvent(new CustomEvent("feedback:request-updated", { detail: { request: data.request } }))
    } catch (_) { /* the next page load shows it */ }
  }

  _commentBody() {
    const f = this.form, title = f.title.trim(), desc = f.desc.trim()
    if (!desc) return title
    return title && f.titleTouched && !desc.toLowerCase().includes(title.toLowerCase()) ? `${title}\n\n${desc}` : desc
  }

  // What Send and Save draft both post: the fields, the files already up,
  // the ones still uploading, and attachments taken off a resumed draft.
  _payload(late) {
    const files = this.form.files.filter(f => f.status === "done").map(f => ({ cache_name: f.cache_name, filename: f.name, content_type: f.type, byte_size: f.size, role: f.role }))
    const pending_uploads = late.map(f => ({ filename: f.name, role: f.role, byte_size: f.size }))
    // The AI hasn't written the recording up yet: what it may still fill in
    // (not what the person chose). Its write-up, the panel's or the server's, lands after.
    const f = this.form
    // `now`: the panel already tried and failed, so the server needn't wait to see if it will.
    const ai_pending = this._aiPending() ? { title: !f.titleTouched, kind: !f.kindTouched, priority: !f.prioTouched, ...(f.rec.summary === null ? { now: true } : {}) } : null
    return { kind: f.kind, title: f.title.trim(), problem: f.desc.trim(), customer_priority: f.prio,
             context: this._context(), files, pending_uploads, remove_attachment_ids: f.removeIds || [], ai_pending,
             ...(this.source ? { source: this.source } : {}) }
  }

  async _uploadShot() {
    if (!this.form.shot?.blob || this.form.files.some(f => f.role === "screenshot")) return
    const entry = { name: `screenshot-${Date.now()}.jpg`, size: this.form.shot.blob.size, type: "image/jpeg", role: "screenshot", status: "uploading" }
    this.form.files.push(entry); this._renderChips()
    await this._upload(entry, this.form.shot.blob); this._renderChips()
  }

  // ── drafts (Phelipe 2026-09-23) ─────────────────────────────────────
  // Save draft keeps everything on the server, visible only to its author;
  // it comes back from "Your recent feedback" and Send turns it into a
  // real request. Uploads still running and a recording's AI write-up land
  // on the draft exactly as they would on a sent request.
  async saveDraft() {
    const t = this.i18nValue, b = this.bodyTarget, btn = b.querySelector("[data-act=draft]"), err = b.querySelector("[data-err]")
    if (!this.form.desc.trim() && !this.form.title.trim() && !this.form.files.length && !this.form.shot) {
      err.textContent = t.describe; err.classList.remove("hidden"); b.querySelector("[data-field=desc]")?.focus(); return
    }
    err.classList.add("hidden")
    const label = btn.textContent
    btn.disabled = true; btn.textContent = t.saving_draft || "Saving…"
    try {
      await this._uploadShot()
      const late = this.form.files.filter(f => f.status === "uploading")
      const draftId = this.form.draftId
      const res = await fetch(draftId ? `${this.createUrlValue}/${draftId}` : this.createUrlValue, { method: draftId ? "PATCH" : "POST", headers: this._headers(),
        body: JSON.stringify({ ...this._payload(late), draft: true }) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.request?.id) throw new Error(data.error || t.upload_failed)
      const id = data.request.id
      this._announce(data.request, true)
      const rec = this.form.rec
      if (rec && rec.summary === undefined && rec.entry?.status !== "failed") rec.sent = { id, kindTouched: this.form.kindTouched, prioTouched: this.form.prioTouched, titleTouched: this.form.titleTouched }
      if (rec?.url) URL.revokeObjectURL(rec.url)
      late.forEach(e => e.promise?.then(ok => this._attachLate(id, e, ok)))
      this._placeRow(data.row_html, id)
      this.form = this._blankForm()
      this._setState("choose")
      this._toast(t.draft_saved || "Draft saved. Pick it up any time under Your recent feedback.")
    } catch (e) {
      err.textContent = e.message || t.upload_failed; err.classList.remove("hidden")
      btn.disabled = false; btn.textContent = label
    }
  }

  async resumeDraft(id) {
    const t = this.i18nValue
    let r = null
    try {
      const res = await fetch(`${this.createUrlValue}/${id}`, { headers: { Accept: "application/json" } })
      r = res.ok ? (await res.json())?.request || null : null
    } catch (e) { r = null }
    if (!r || !r.draft) { this._toast(t.draft_load_failed || "Couldn't open that draft", "alert"); return }
    const f = this._blankForm()
    Object.assign(f, { draftId: r.id, kind: KINDS.includes(r.kind) ? r.kind : "feature", prio: PRIOS.includes(r.customer_priority) ? r.customer_priority : "medium",
                       title: r.title || "", desc: r.problem || "", titleTouched: !!r.title, kindTouched: true, prioTouched: true })
    // Files already on the draft: shown as chips; × takes one off at the next save.
    f.files = (r.attachments || []).map(a => ({ name: a.name || "file", size: 0, type: "", role: a.kind, status: "attached", attachmentId: a.id }))
    this.form = f
    this._setState("text")
  }

  // From a draft row on the list. Never throws away what's being written:
  // a recording in progress or unsaved words in the panel come first.
  async resumeFromList(id) {
    const t = this.i18nValue
    if (this.recording) { this._toast(t.finish_recording_first || "Stop the recording first", "alert"); return }
    const f = this.form
    const unsaved = this.panelOpen && ["text", "shot", "rec"].includes(this.state) && f.draftId !== id &&
                    (f.desc.trim() || f.title.trim() || f.files.length || f.shot)
    this.open()
    if (unsaved) { this._toast(t.finish_current_first || "Save or send what you're writing first", "alert"); return }
    await this.resumeDraft(id)
  }

  // ── Copy public link (Phelipe 2026-09-24): the request's /feedback/p/<token> page, to share anywhere ──
  async _copyLink(url, btn) {
    const t = this.i18nValue
    if (!url) { this._toast(t.link_failed, "alert"); return }
    let ok = false
    try { await navigator.clipboard.writeText(url); ok = true } catch (_) { ok = this._legacyCopy(url) }
    if (!ok) { this._toast(t.link_failed, "alert"); return }
    if (btn) { const was = btn.textContent; btn.textContent = t.link_copied; setTimeout(() => { btn.textContent = was }, 2000) }
    this._toast(t.link_copied)
  }

  _legacyCopy(text) {
    const ta = document.createElement("textarea")
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.cssText = "position:fixed;left:-9999px;top:0"
    document.body.appendChild(ta); ta.select()
    let ok = false
    try { ok = document.execCommand("copy") } catch (_) { ok = false }
    ta.remove()
    return ok
  }

  // ── the Feedback Loop list, when that page is open ─────────────────
  // Save draft, Send and Discard land on the page at once (house rule: no
  // reloads). The list then re-runs its own filters and counts.
  _feedbackList() {
    const el = document.querySelector('[data-controller~="feedback-list"]')
    if (!el) return null
    return { el, rows: el.querySelector('[data-feedback-list-target="rows"]'),
             ctl: this.application.getControllerForElementAndIdentifier(el, "feedback-list") }
  }

  _placeRow(html, id) {
    const list = this._feedbackList()
    if (!list?.rows || !html) return
    list.rows.querySelector(`[data-feedback-list-target="row"][data-id="${id}"]`)?.remove()
    const tpl = document.createElement("template")
    tpl.innerHTML = html.trim()
    const row = tpl.content.firstElementChild
    if (!row) return
    // One waiting for a reviewer (K8-31) goes into the Pending review box instead.
    if (row.dataset.feedbackReviewTarget === "row") {
      const review = this.application.getControllerForElementAndIdentifier(list.el, "feedback-review")
      if (review?.place) review.place(row)
      return
    }
    // Drafts sit on top; a sent request goes first among the sent ones.
    const firstSent = [...list.rows.querySelectorAll('[data-feedback-list-target="row"]')].find(r => !r.dataset.draft)
    if (row.dataset.draft) list.rows.prepend(row)
    else if (firstSent) list.rows.insertBefore(row, firstSent)
    else list.rows.appendChild(row)
    list.ctl?.filter?.()
  }

  _dropRow(id, trashHtml) {
    const list = this._feedbackList()
    if (!list) return
    list.rows?.querySelector(`[data-feedback-list-target="row"][data-id="${id}"]`)?.remove()
    const trash = list.el.querySelector('[data-feedback-list-target="trashList"]')
    if (trash && trashHtml) {
      trash.insertAdjacentHTML("afterbegin", trashHtml)
      list.el.querySelector('[data-feedback-list-target="trashEmpty"]')?.classList.add("hidden")
      const n = list.el.querySelector('[data-feedback-list-target="trashCount"]')
      if (n) n.textContent = String((parseInt(n.textContent, 10) || 0) + 1)
    }
    list.ctl?.filter?.()
  }

  // House rule: a real confirm with a backdrop, never the browser's.
  _askDiscard() {
    const t = this.i18nValue, esc = this._esc
    this.panelTarget.querySelector("[data-discard-modal]")?.remove()
    const m = document.createElement("div")
    m.setAttribute("data-discard-modal", "")
    m.className = "absolute inset-0 z-20 flex items-center justify-center bg-black/30 p-4"
    m.innerHTML = `<div class="w-full max-w-xs rounded-xl bg-white p-4 shadow-xl" data-discard-card>
        <h4 class="m-0 text-[15px] font-semibold text-gray-900">${esc(t.discard_title || "Discard this draft?")}</h4>
        <p class="m-0 mt-1 text-[13px] text-gray-600">${esc(t.discard_body || "It moves to the trash on the Feedback page, where you can restore it.")}</p>
        <div class="mt-3 flex justify-end gap-2">
          <button type="button" class="rounded-lg px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100 cursor-pointer" data-discard-no>${esc(t.cancel || "Cancel")}</button>
          <button type="button" class="rounded-lg bg-red-600 hover:bg-red-700 px-3 py-1.5 text-[13px] font-semibold text-white cursor-pointer" data-discard-yes>${esc(t.discard || "Discard")}</button>
        </div></div>`
    m.onclick = (e) => { if (!e.target.closest("[data-discard-card]")) m.remove() }
    m.querySelector("[data-discard-no]").onclick = () => m.remove()
    m.querySelector("[data-discard-yes]").onclick = () => { m.remove(); this.discardDraft() }
    this.panelTarget.appendChild(m)
  }

  async discardDraft() {
    const t = this.i18nValue, id = this.form.draftId
    if (!id) return
    let data = null
    try {
      const res = await fetch(`${this.createUrlValue}/${id}`, { method: "DELETE", headers: this._headers() })
      data = res.ok ? await res.json().catch(() => ({})) : null
    } catch (e) { data = null }
    if (!data) { this._toast(t.discard_failed || "Couldn't discard the draft", "alert"); return }
    this._dropRow(id, data.trash_html)
    if (this.form.rec?.url) URL.revokeObjectURL(this.form.rec.url)
    this.form = this._blankForm()
    this._setState("choose")
    this._toast(t.draft_discarded || "Draft discarded")
  }

  // A file that finished (or failed) after Send: register it on the request
  // so the row and the drawer swap "still loading" for the file itself.
  async _attachLate(id, entry, ok) {
    const t = this.i18nValue
    const body = ok ? { files: [{ cache_name: entry.cache_name, filename: entry.name, content_type: entry.type, byte_size: entry.size, role: entry.role }] } : { failed: [entry.name] }
    let r = null
    try {
      const res = await fetch(`${this.createUrlValue}/${id}/attach`, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = res.ok ? await res.json() : null
      r = data?.request || null
    } catch (e) { r = null }
    entry.late = ok && r ? "added" : "failed"
    if (r) this._broadcast(r)
    const what = entry.role === "recording" ? t.the_recording : entry.name
    this._toast((entry.late === "added" ? t.late_added : t.late_failed).replace("%{name}", what), entry.late === "added" ? "notice" : "alert")
    this._renderLate()
  }

  // The recording's AI write-up arrived after Send: put it on the request
  // (fills a blank description, follows a typed one; title/type/priority
  // only where the person didn't choose).
  async _summaryLate(rec, s) {
    const { id, kindTouched, prioTouched, titleTouched } = rec.sent
    const body = { ai_summary: s.summary || "", ai_title: titleTouched ? "" : (s.title || ""), ai_kind: kindTouched ? "" : (s.kind || ""), ai_priority: prioTouched ? "" : (s.priority || "") }
    try {
      const res = await fetch(`${this.createUrlValue}/${id}`, { method: "PATCH", headers: this._headers(), body: JSON.stringify(body) })
      const data = res.ok ? await res.json() : null
      if (data?.request) { this._broadcast(data.request); this._toast(this.i18nValue.summary_added) }
    } catch (e) { /* the recording itself is on the request */ }
  }

  // The Feedback page (if it's open) repaints the row and the drawer.
  _broadcast(r) { window.dispatchEvent(new CustomEvent("feedback:request-updated", { detail: { request: r } })) }

  // Whoever hosts the form (the chat window) hears that a request went out or a
  // draft was kept, and can say so in the conversation (Phelipe 2026-10-17).
  _announce(request, draft) {
    if (!request) return
    window.dispatchEvent(new CustomEvent("instant-feedback:sent", { detail: { request, draft, host: this._host?.name || null } }))
  }

  _lateHtml() {
    const t = this.i18nValue, L = this.late
    if (!L?.entries?.length) return ""
    const rows = L.entries.map(e => {
      const st = e.late || "loading", what = e.role === "recording" ? t.the_recording : e.name
      const icon = st === "loading" ? `<span class="inline-block w-3.5 h-3.5 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin"></span>`
        : st === "added" ? `<span class="text-green-700">${this._icon("ok")}</span>` : `<span class="text-red-700 font-bold">!</span>`
      const text = (st === "loading" ? t.still_uploading : st === "added" ? t.late_added : t.late_failed).replace("%{name}", what)
      return `<div class="flex items-start gap-2 text-left"><span class="shrink-0 mt-px">${icon}</span><span>${this._esc(text)}</span></div>`
    }).join("")
    return `<div class="flex flex-col gap-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2.5 text-[12.5px] mb-3" data-late>${rows}</div>`
  }

  _renderLate() { const el = this.bodyTarget.querySelector("[data-late]"); if (el) el.outerHTML = this._lateHtml() }

  async _loadRecent() {
    const box = this.bodyTarget.querySelector("[data-recent]")
    if (!box) return
    try {
      const res = await fetch(`${this.listUrlValue}?mine=1&limit=3`, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const { requests } = await res.json()
      const t = this.i18nValue
      if (!requests?.length || box !== this.bodyTarget.querySelector("[data-recent]")) return
      box.innerHTML = `<h4 class="m-0 mt-1 text-[12px] font-bold uppercase tracking-wide text-gray-600">${this._esc(t.recent)}</h4>
        <div class="flex flex-col gap-1.5 mt-1.5">${requests.map(r => r.draft
          ? `<button type="button" class="flex items-center justify-between gap-2 w-full text-left rounded-lg border border-amber-200 bg-amber-50 hover:border-amber-400 px-2.5 py-2 text-[13px] cursor-pointer" data-resume="${r.id}"><span class="truncate min-w-0">${this._esc(r.title)}</span><span class="inline-block shrink-0 text-[12px] font-semibold px-2 py-px rounded-full bg-amber-100 text-amber-900">${this._esc(t.draft_pill || "Draft")}</span></button>`
          : `<div class="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-[13px]"><span class="truncate min-w-0">${this._esc(r.title)}</span>${r.pending_uploads?.length ? `<span class="inline-block shrink-0 w-3 h-3 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin" title="${this._esc(t.still_uploading)}"></span>` : ""}<span class="inline-block shrink-0 text-[12px] font-semibold px-2 py-px rounded-full ${t.status_pill[r.status] || ""}">${this._esc(t.status_notes?.[r.status] ? t.statuses.done : t.statuses[r.status] || r.status)}</span>${t.status_notes?.[r.status] ? `<span class="shrink-0 text-[12px] text-gray-500">(${this._esc(t.status_notes[r.status])})</span>` : ""}</div>`).join("")}</div>
        <button type="button" class="w-full mt-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold py-2 cursor-pointer" data-act="seeAll">${this._esc(t.see_all)}</button>`
      box.querySelector("[data-act=seeAll]").onclick = () => this._act("seeAll")
      box.querySelectorAll("[data-resume]").forEach(el => { el.onclick = () => this.resumeDraft(+el.dataset.resume) })
    } catch (e) { /* offline: the list just doesn't show */ }
  }

  // ── bits ────────────────────────────────────────────────────────────
  _icon(name) {
    const p = {
      pencil: '<path d="M4 20h4l10-10-4-4L4 16z M12 8l4 4"/>',
      video: '<rect x="3" y="6" width="13" height="12" rx="2"/><path d="M16 10.5l5-3v9l-5-3"/>',
      text: '<path d="M5 7V5h14v2M12 5v14M9 19h6"/>',
      pin: '<path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>',
      check: '<path d="M5 12l5 5L20 7"/>',
      ok: '<path d="M5 12l5 5L20 7"/>',
      spark: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/>',
      file: '<path d="M6 3h8l5 5v13H6z M14 3v5h5"/>'
    }[name]
    const size = name === "check" ? 28 : name === "pencil" || name === "video" || name === "text" ? 22 : 14
    return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="${name === "check" ? 2.5 : 1.9}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="shrink-0">${p}</svg>`
  }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  _headers() {
    return { "Content-Type": "application/json", Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
  }

  // ── the panel's own log and watchdog (Kloepfer K13-28) ───────────────
  // Every step of a recording and a Send goes in a short log (kept per tab,
  // so a page load doesn't lose it) and to the server's log a few seconds
  // later. A step that fails, or takes longer than WATCH_MS, also posts the
  // problem with the log, what they wrote and their recording: ConsLog files
  // it for the team itself (FeedbackLoopIssueReporter). Its own line to the
  // server, apart from Send, so it gets through when Send is what's broken.
  _loadSteps() {
    try { return JSON.parse(this._session(STEP_LOG) || "[]").slice(-60) } catch (_) { return [] }
  }

  _step(step, detail = "") {
    const entry = { at: new Date().toISOString(), step, detail: String(detail ?? "").slice(0, 200) }
    this._steps = (this._steps || []).concat(entry).slice(-60)
    this._session(STEP_LOG, JSON.stringify(this._steps))
    ;(this._unsent ||= []).push(entry)
    clearTimeout(this._flushTimer)
    this._flushTimer = setTimeout(() => this._flushSteps(), 4000)
  }

  _flushSteps(extra = null) {
    if (!this.recorderUrlValue) return Promise.resolve(false)
    const steps = this._unsent || []
    this._unsent = []
    if (!steps.length && !extra) return Promise.resolve(false)
    return fetch(this.recorderUrlValue, { method: "POST", headers: this._headers(), keepalive: true, credentials: "same-origin",
      body: JSON.stringify({ steps: extra ? (this._steps || []).slice(-40) : steps, ...(extra || {}) }) })
      .then(res => res.ok).catch(() => false)
  }

  // Runs `slow` and tells the team once `ms` go by; call what it returns when the step is through.
  _watch(step, ms, slow) {
    const started = Date.now()
    const timer = setTimeout(() => {
      this._step(`${step}_slow`, `${Math.round((Date.now() - started) / 1000)}s`)
      slow?.()
      this._reportIssue(step, `took longer than ${Math.round(ms / 1000)} seconds`, Math.round((Date.now() - started) / 1000))
    }, ms)
    return () => clearTimeout(timer)
  }

  // Once per step for each recording (or each Send without one).
  _reportIssue(step, error, seconds = null) {
    const f = this.form || {}
    const key = `${step}:${f.rec?.name || f.desc?.slice(0, 40) || ""}`
    if (this._reported?.has(key)) return
    this._reported?.add(key)
    const files = (f.files || []).filter(x => x.status === "done" && x.cache_name).map(x => ({ cache_name: x.cache_name, filename: x.name, role: x.role }))
    this._step(`${step}_reported`, error)
    clearTimeout(this._flushTimer)
    this._unsent = []
    this._flushSteps({
      issue: { step, error: String(error || "").slice(0, 300), seconds, surface: this._surface || "", browser: (navigator.userAgent || "").slice(0, 200),
               page: document.title || "", path: location.pathname, title: f.title || "", desc: f.desc || "" },
      files
    })
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "4500")
    el.textContent = message
    document.body.prepend(el)
  }
}
